import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Map browser / i18n tags (e.g. pt-BR) to API-supported codes for achievement copy. */
function normalizeQuizterLanguageHeader(raw: string | null | undefined): 'en' | 'pt' | null {
  if (raw == null || !String(raw).trim()) return null;
  const lower = String(raw).trim().toLowerCase();
  if (lower.startsWith('pt')) return 'pt';
  if (lower.startsWith('en')) return 'en';
  return null;
}

// Request interceptor to add auth token and UI language (achievement strings match selector)
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const stored = localStorage.getItem('quizter-language');
    const fromBrowser =
      typeof navigator !== 'undefined' && navigator.language ? navigator.language : null;
    const uiLang = normalizeQuizterLanguageHeader(stored) ?? normalizeQuizterLanguageHeader(fromBrowser);
    if (uiLang) {
      config.headers['X-Quizter-Language'] = uiLang;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('userId');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export interface LivesStatus {
  lives: number;
  maxLives: number;
  serverTimeUtc: string;
  nextRegenerationAt: string | null;
  secondsUntilNext: number | null;
}

/** Active inventory XP potion buff (from profile; same shape as inventory activeXpBoost). */
export interface UserActiveXpBoost {
  bonusPercent: number;
  expiresAtUtc: string;
  /** Full minutes left, computed on the server from UTC (may be absent on stale cached payloads). */
  minutesRemaining?: number;
}

export interface User {
  id: string;
  email: string;
  name: string;
  nickname: string | null;
  /** API uses "Vip"; "Premium" may appear on legacy sessions. */
  userType: 'Free' | 'Vip' | 'Admin' | 'Premium' | 1 | 2 | 3;
  level: number;
  experiencePoints: number;
  lives: number;
  livesStatus?: LivesStatus;
  requiresNickname: boolean;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  /** ISO UTC when paid VIP ends; only set for UserType Vip. */
  vipExpiresAtUtc?: string | null;
  /** XP potion buff until expiresAtUtc (null if none). */
  activeXpBoost?: UserActiveXpBoost | null;
  /** When false, clients should not play quiz feedback SFX. Default true if omitted (legacy). */
  quizSoundsEnabled?: boolean;
  /** When set by GET /user/profile, overrides local background music preference. */
  backgroundMusicEnabled?: boolean;
}

export interface ExperienceInfo {
  currentLevel: number;
  totalXP: number;
  xpForNextLevel: number;
  xpProgress: number;
  requiredXPForCurrentLevel: number;
  requiredXPForNextLevel: number;
  xpInCurrentLevel: number;
  xpNeededForNextLevel: number;
}

export interface AdminDashboard {
  totalUsers: number;
  activeUsers: number;
  adminUsers: number;
  vipUsers: number;
  freeUsers: number;
}

export interface MostPlayedQuizReportItem {
  quizId: string;
  title: string;
  /** Optional subtitle / franchise line (same as quiz lobby “theme”). */
  theme?: string | null;
  playCount: number;
}

export interface MostPlayedQuizzesReportResponse {
  items: MostPlayedQuizReportItem[];
}

/** One bucket of the user-signups report. `bucketStartUtc` is an ISO-8601 UTC string. */
export interface UserSignupsReportBucket {
  bucketStartUtc: string;
  count: number;
}

/** Series of equally-sized buckets covering [fromUtc, toUtc). */
export interface UserSignupsReportSeries {
  /** "hour" for the 24h window, "day" for the 7d / 30d windows. */
  granularity: 'hour' | 'day';
  fromUtc: string;
  toUtc: string;
  total: number;
  buckets: UserSignupsReportBucket[];
}

/** Three series in one payload — one round trip powers the whole tab. */
export interface UserSignupsReportResponse {
  last24Hours: UserSignupsReportSeries;
  last7Days: UserSignupsReportSeries;
  last30Days: UserSignupsReportSeries;
}

/** Public VIP offer (GET /vip/pricing, no auth). */
export interface VipPricingPublic {
  originalPriceBrl: number;
  promoPriceBrl: number;
  durationDays: number;
  discountPercent: number;
}

export interface VipPricingAdmin extends VipPricingPublic {
  updatedAtUtc: string;
  asyncPvPLobbyMinJoinableRooms: number;
}

/** Card Payment Brick payload (snake_case fields from @mercadopago/sdk-react). */
export interface VipCardPaymentBrickPayload {
  token: string;
  issuer_id: string;
  payment_method_id: string;
  transaction_amount: number;
  installments: number;
  payer?: {
    email?: string;
    identification?: { type?: string; number?: string };
  };
}

export interface VipCardPaymentResponse {
  message: string;
  paymentId?: number;
  token: string;
  user: {
    id: string;
    email: string;
    name: string;
    nickname?: string | null;
    userType: string;
    level: number;
    experiencePoints: number;
    lives: number;
    vipExpiresAtUtc?: string | null;
  };
}

export const vipService = {
  getPublicPricing: async (): Promise<VipPricingPublic> => {
    const response = await api.get('/vip/pricing');
    return response.data;
  },

  processCardPayment: async (formData: VipCardPaymentBrickPayload): Promise<VipCardPaymentResponse> => {
    const response = await api.post('/vip/payment/card', {
      token: formData.token,
      issuerId: formData.issuer_id,
      paymentMethodId: formData.payment_method_id,
      installments: formData.installments,
      transactionAmount: formData.transaction_amount,
      payer: formData.payer
        ? {
            email: formData.payer.email,
            identification: formData.payer.identification,
          }
        : undefined,
    });
    return response.data;
  },
};

export interface Quiz {
  id: string;
  title: string;
  /** English title — WITC bilingual quizzes (required on server for WITC). */
  titleEn?: string | null;
  /** Optional second line on lobby cards (primary quiz language). */
  theme?: string | null;
  /** Optional English second line for WITC lobby cards. */
  themeEn?: string | null;
  difficulty: string;
  questionCount: number;
  /** Optional lobby card image: basename; files at `public/assets/preview/{key}.webp` or `.png`. */
  imageUrl?: string;
  language: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  totalQuestions: number;
  canActivate: boolean;
  scorePerCorrectAnswer: number;
  baseScore: number;
  /** Normalized tags on the quiz (e.g. achievements / filtering). */
  tags?: string[];
  /** MultipleChoice (default) or Witc — Who Is This Character (image + text). */
  format?: string;
  /** Subfolder under `public/assets/images/` for WITC question images (`.webp` or `.png` per key). */
  witcImageAssetsBasePath?: string | null;
  /** Subfolder under `public/assets/images/` for optional MC question images (same tree as WITC). */
  mcImageAssetsBasePath?: string | null;
  /** MC: show answer options as images from `mcAnswerImageAssetsBasePath` when keys are set. */
  mcUseImageAnswers?: boolean;
  /** Subfolder under `public/assets/` for MC answer-option images. */
  mcAnswerImageAssetsBasePath?: string | null;
  /** Genius: image folder under `public/assets/images/`. */
  geniusImageAssetsBasePath?: string | null;
  /** Genius: sound folder under `public/assets/sounds/`. */
  geniusSoundAssetsBasePath?: string | null;
  tileMatchImageAssetsBasePath?: string | null;
  /** Solo listing: sort first + badge when marked popular in admin. */
  recommendedSolo?: boolean;
  /** Career unlock reward candidate; lobbies show a padlock. */
  isUnlockable?: boolean;
  /** Optional character/question unlock chain within this quiz (prerequisite question + correct counts). */
  characterUnlockEnabled?: boolean;
  /**
   * Solo lobby: há pré-visualização no modal de personagens (cadeia e/ou perguntas só Médio-Difícil com imagem),
   * mesmo quando `characterUnlockEnabled` está desligado no cadastro.
   */
  characterUnlockLobbyPreview?: boolean;
  /** Solo/PvP catalog: true quando o utilizador já desbloqueou via missão (ou o quiz não é `isUnlockable`). */
  careerUnlockedForUser?: boolean;
  /** WITC/MC: quiz inclui respostas para modo fácil no cadastro. */
  supportsPlayDifficultyEasy?: boolean;
  /** WITC/MC: quiz inclui respostas para modo médio (e difícil reutiliza médio). */
  supportsPlayDifficultyMedium?: boolean;
  supportsPlayDifficultyHard?: boolean;
  /** Solo: Medium selectable only after Career unlock when the quiz supports medium. */
  playMediumUnlockedForUser?: boolean;
  /** Solo: Hard selectable only after Career unlock when the quiz supports hard. */
  playHardUnlockedForUser?: boolean;
  /** Admin: reasons activation is blocked when canActivate is false. */
  activationBlockers?: string[] | null;
}

export interface CreateQuiz {
  title: string;
  titleEn?: string | null;
  theme?: string | null;
  themeEn?: string | null;
  difficulty: string;
  questionCount: number;
  /** Basename under `public/assets/preview/` (.webp / .png). */
  imageUrl?: string;
  language: string;
  tags?: string[];
  format?: string;
  witcImageAssetsBasePath?: string | null;
  mcImageAssetsBasePath?: string | null;
  mcUseImageAnswers?: boolean;
  mcAnswerImageAssetsBasePath?: string | null;
  geniusImageAssetsBasePath?: string | null;
  geniusSoundAssetsBasePath?: string | null;
  tileMatchImageAssetsBasePath?: string | null;
  recommendedSolo?: boolean;
  isUnlockable?: boolean;
  characterUnlockEnabled?: boolean;
  supportsPlayDifficultyEasy?: boolean;
  supportsPlayDifficultyMedium?: boolean;
  supportsPlayDifficultyHard?: boolean;
}

export interface UpdateQuiz {
  title: string;
  titleEn?: string | null;
  theme?: string | null;
  themeEn?: string | null;
  difficulty: string;
  questionCount: number;
  /** Basename under `public/assets/preview/` (.webp / .png). */
  imageUrl?: string;
  language: string;
  /** Send [] to clear tags; omit only if not changing (UI always sends). */
  tags?: string[];
  format?: string;
  witcImageAssetsBasePath?: string | null;
  mcImageAssetsBasePath?: string | null;
  mcUseImageAnswers?: boolean;
  mcAnswerImageAssetsBasePath?: string | null;
  geniusImageAssetsBasePath?: string | null;
  geniusSoundAssetsBasePath?: string | null;
  tileMatchImageAssetsBasePath?: string | null;
  recommendedSolo?: boolean;
  isUnlockable?: boolean;
  characterUnlockEnabled?: boolean;
  supportsPlayDifficultyEasy?: boolean;
  supportsPlayDifficultyMedium?: boolean;
  supportsPlayDifficultyHard?: boolean;
}

export interface Question {
  id: string;
  quizId: string;
  text: string;
  createdAt: string;
  updatedAt: string;
  answers: Answer[];
  /** Extra question-only tags (quiz tags apply in addition). */
  tags?: string[];
  textEn?: string | null;
  imageAssetKey?: string | null;
  soundAssetKey?: string | null;
  correctAnswerPt?: string | null;
  correctAnswerEn?: string | null;
  correctAnswerEasyPt?: string | null;
  correctAnswerEasyEn?: string | null;
  correctAnswerMediumPt?: string | null;
  correctAnswerMediumEn?: string | null;
  excludeFromEasyDifficulty?: boolean;
  unlockPrerequisiteQuestionId?: string | null;
  unlockPrerequisiteCorrectCount?: number;
  unlockPrerequisiteQuestionPreviewText?: string | null;
  /** Witc Easy/Medium play: underscore skeleton from that band’s answers (PT/EN). */
  witcMediumSkeletonPt?: string | null;
  witcMediumSkeletonEn?: string | null;
  /** Solo quick match: image folder basename for this question’s source quiz. */
  soloQuestionImageBasePath?: string | null;
  /** Solo quick match: source quiz had MC image answers enabled. */
  soloMcUseImageAnswers?: boolean | null;
  /** Solo quick match: answer-image folder under public/assets/ from source quiz. */
  soloMcAnswerImageBasePath?: string | null;
}

/** Route segment for `/quiz/:id` when playing solo Partida rápida (must match App route). */
export const SOLO_QUICK_MATCH_ROUTE_ID = 'quick-match';

/** Shell quiz id from POST quick-match/start — must match API `SoloQuickMatchConstants.PlaceholderQuizId`. */
export const SOLO_QUICK_MATCH_PLACEHOLDER_QUIZ_ID = 'f8e3d2c1-b5a4-4c6d-9e0f-123456789abc';

/** POST /api/quiz/{id}/start — solo play with life debit. */
export interface SoloQuizStartResponse {
  quiz: Quiz;
  questions: Question[];
  livesStatus: LivesStatus;
  /** Witc: scope validate-answer wrong counts to this run — required for solo Witc validates. */
  witcPlaySessionId?: string | null;
  /** Band used for this run (echo server-resolved choice); send on save attempt when quiz supports multiple modes. */
  playDifficulty?: string | null;
  /** True when started via POST /quiz/quick-match/start. */
  isSoloQuickMatch?: boolean;
  /** Same length as questions — server-resolved band per slot. */
  playDifficultiesPerQuestion?: string[] | null;
  /** Genius solo: panel, rounds sequence, play session. */
  genius?: GeniusPlayStart | null;
  /** Tile Match solo: round 1 board and play session. */
  tileMatch?: TileMatchPlayStart | null;
  /** Word Connect solo: crossword layout and letter pool. */
  wordConnect?: WordConnectPlayStart | null;
  /** Witc: scope validate-answer local engine bootstrap package. */
  witcEngineBootstrapCipher?: string | null;
  /** Stop solo: encrypted accepted answers per letter. */
  stopEngineBootstrapCipher?: string | null;
  mcPlaySessionId?: string | null;
  mcEngineBootstrapCipher?: string | null;
}

export interface GeniusPanelItem {
  questionId: string;
  imageAssetKey: string;
  soundAssetKey: string;
  label?: string | null;
}

export interface GeniusPlayStart {
  playSessionId: string;
  panel: GeniusPanelItem[];
  /** 10 rounds; each entry is ordered question ids to play and answer. */
  rounds: string[][];
  totalRounds: number;
  engineBootstrapCipher?: string | null;
}

export interface TileMatchTileDto {
  id: string;
  imageAssetKey: string;
  questionId?: string;
  x: number;
  y: number;
  z: number;
  isBlocked: boolean;
}

export interface TileMatchRoundDto {
  roundIndex: number;
  tiles: TileMatchTileDto[];
  distinctKeyCount: number;
  triosPerKey: number;
  totalTiles: number;
  maxLayers: number;
}

export interface TileMatchPlayStart {
  playSessionId: string;
  imageAssetsBasePath: string;
  round?: TileMatchRoundDto | null;
  totalRounds: number;
  engineBootstrapCipher?: string;
}

export interface TileMatchFinishAudit {
  playSessionId: string;
  quizId: string;
  actions: Array<{
    actionType: string;
    tileId?: string | null;
    timestampUtcMs: number;
    roundIndex: number;
  }>;
  clientTotalScore: number;
  pointsPerRound: number[];
  roundRepresentativeQuestionIds: string[];
  signature: string;
}

export interface GeniusFinishAudit {
  playSessionId: string;
  quizId: string;
  rounds: Array<{
    roundIndex: number;
    picksInOrder: string[];
    isCorrect: boolean;
    pointsEarned: number;
    timestampUtcMs: number;
  }>;
  clientTotalScore: number;
  signature: string;
}

export interface SubmitGeniusRoundResponse {
  correct: boolean;
  gameOver: boolean;
  sessionComplete: boolean;
  pointsEarnedThisRound: number;
  totalScore: number;
  nextRoundIndex: number;
  expectedPickCount: number;
  totalRounds: number;
}

export interface TileMatchPlayState {
  currentRound: number;
  board: TileMatchTileDto[];
  selectionBar: TileMatchTileDto[];
  evacuateHold: TileMatchTileDto[];
  isGameOver: boolean;
  isRoundWon: boolean;
  isRunWon: boolean;
  sessionComplete: boolean;
  pointsEarnedThisRound: number;
  totalScore: number;
  pointsPerRound?: number[];
  nextRound?: TileMatchRoundDto | null;
  shuffleUsed: boolean;
  undoUsed: boolean;
  evacuateUsed: boolean;
}

export interface WordConnectWordDto {
  questionId: string;
  /** Omitted during play — use {@link length} for board geometry. */
  text: string;
  length: number;
  startRow: number;
  startCol: number;
  isHorizontal: boolean;
  isCompleted: boolean;
}

export interface WordConnectCellDto {
  row: number;
  col: number;
  letter: string | null;
  wordColorIndex?: number | null;
}

export interface WordConnectLetterSlotDto {
  slotId: string;
  letter: string;
  sourceQuestionId?: string;
  wordColorIndex?: number;
}

export interface WordConnectPlayStart {
  playSessionId: string;
  /** Same order as WASM pointsPerWordSlot / witcResults for this session. */
  activeQuestionIds?: string[];
  activeWordCount: number;
  initialTimerSeconds: number;
  timeBonusPerLetterSeconds: number;
  timeBonusPerWordSeconds: number;
  pointsPerWord: number;
  /** Opaque cipher for WASM — never decode in TypeScript. */
  engineBootstrapCipher?: string;
  words: WordConnectWordDto[];
  cells: WordConnectCellDto[];
  letterPool: WordConnectLetterSlotDto[];
}

export interface WordConnectFinishAudit {
  playSessionId: string;
  quizId: string;
  placements: Array<{
    slotId: string;
    row: number;
    col: number;
    timestampUtcMs: number;
  }>;
  clientTotalScore: number;
  pointsPerWordSlot: number[];
  signature: string;
}

export interface WitcSubmissionLogEntry {
  questionId: string;
  submissionIndex: number;
  submittedText: string;
  isCorrect: boolean;
  errorCountAfter: number;
  timestampUtcMs: number;
  pointsEarned: number;
}

export interface WitcFinishAudit {
  playSessionId: string;
  quizId: string;
  submissions: WitcSubmissionLogEntry[];
  clientTotalScore: number;
  pointsPerQuestion: number[];
  signature: string;
}

export interface StopSubmissionLogEntry {
  questionId: string;
  submittedText: string;
  isCorrect: boolean;
  isDuplicate: boolean;
  matchedAnswerId?: string | null;
  foundCountAfter: number;
  wrongCountAfter: number;
  timestampUtcMs: number;
  pointsEarned: number;
}

export interface StopFinishAudit {
  playSessionId: string;
  quizId: string;
  submissions: StopSubmissionLogEntry[];
  clientTotalScore: number;
  pointsPerQuestion: number[];
  signature: string;
}

export interface McSubmissionLogEntry {
  questionId: string;
  chosenAnswerId: string;
  isCorrect: boolean;
  pointsEarned: number;
  timestampUtcMs: number;
}

export interface McSubmissionResult {
  isCorrect: boolean;
  pointsEarned: number;
  correctAnswerId: string;
  isSessionComplete: boolean;
}

export interface McFinishAudit {
  playSessionId: string;
  quizId: string;
  submissions: McSubmissionLogEntry[];
  clientTotalScore: number;
  signature: string;
}

export interface McPvpPlayStart {
  mcPlaySessionId: string;
  mcEngineBootstrapCipher: string;
  questions: Question[];
  playDifficulty: string;
}

export interface StopPlayStart {
  playSessionId: string;
  engineBootstrapCipher: string;
  questions: Question[];
}

export interface WitcPlayStart {
  playSessionId: string;
  imageAssetsBasePath: string;
  engineBootstrapCipher: string;
}

export interface WordConnectPlayState {
  words: WordConnectWordDto[];
  cells: WordConnectCellDto[];
  letterPool: WordConnectLetterSlotDto[];
  totalScore: number;
  wordsCompleted: number;
  activeWordCount: number;
  sessionComplete: boolean;
  wordJustCompleted: boolean;
  pointsEarnedThisWord: number;
  pointsPerWordSlot: number[];
  placementAccepted?: boolean;
  placementMessage?: string | null;
}

export interface CreateQuestion {
  quizId: string;
  text: string;
  answers: CreateAnswer[];
  tags?: string[];
  textEn?: string | null;
  imageAssetKey?: string | null;
  soundAssetKey?: string | null;
  correctAnswerPt?: string | null;
  correctAnswerEn?: string | null;
  correctAnswerEasyPt?: string | null;
  correctAnswerEasyEn?: string | null;
  correctAnswerMediumPt?: string | null;
  correctAnswerMediumEn?: string | null;
  excludeFromEasyDifficulty?: boolean;
  unlockPrerequisiteQuestionId?: string | null;
  unlockPrerequisiteCorrectCount?: number;
}

export interface UpdateQuestion {
  text: string;
  answers: UpdateAnswer[];
  tags?: string[];
  textEn?: string | null;
  imageAssetKey?: string | null;
  soundAssetKey?: string | null;
  correctAnswerPt?: string | null;
  correctAnswerEn?: string | null;
  correctAnswerEasyPt?: string | null;
  correctAnswerEasyEn?: string | null;
  correctAnswerMediumPt?: string | null;
  correctAnswerMediumEn?: string | null;
  excludeFromEasyDifficulty?: boolean;
  unlockPrerequisiteQuestionId?: string | null;
  unlockPrerequisiteCorrectCount?: number;
}

export interface Answer {
  id: string;
  questionId: string;
  text: string;
  /** English option label (multiple-choice bilingual). */
  textEn?: string | null;
  /** MC image answer basename (no extension). */
  imageAssetKey?: string | null;
  isCorrect: boolean;
  isCorrectEasy?: boolean;
  isCorrectMedium?: boolean;
}

export interface CreateAnswer {
  text: string;
  textEn?: string | null;
  imageAssetKey?: string | null;
  isCorrect: boolean;
  isCorrectEasy?: boolean;
  isCorrectMedium?: boolean;
}

export interface UpdateAnswer {
  id: string;
  text: string;
  textEn?: string | null;
  imageAssetKey?: string | null;
  isCorrect: boolean;
  isCorrectEasy?: boolean;
  isCorrectMedium?: boolean;
}

export interface LoginResponse {
  message: string;
  token: string;
  user: User;
}

export interface QuizListResponse {
  quizzes: Quiz[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/** Quiz rows from GET /admin/quizzes/compact (admin selects). */
export interface QuizCompactAdminDto {
  id: string;
  /** Backend enum: 0 = MultipleChoice, 1 = Witc (WITC). */
  format?: number;
  title: string;
  titleEn: string | null;
  theme: string | null;
  themeEn: string | null;
  language: string;
  isUnlockable: boolean;
  supportsPlayDifficultyEasy: boolean;
  supportsPlayDifficultyMedium: boolean;
  supportsPlayDifficultyHard: boolean;
}

export interface AdminQuestionListResponse {
  questions: Question[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface AdminUserListResponse {
  users: User[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface AdminAchievementListResponse {
  achievements: AdminAchievement[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface AdminCareerMissionListResponse {
  missions: CareerMissionAdmin[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface QuizFilters {
  page?: number;
  pageSize?: number;
  difficulty?: string;
  search?: string;
  language?: string;
  allLanguages?: boolean;
}

export type AchievementRarity = 'Common' | 'Rare' | 'Epic';

export interface AdminAchievement {
  id: string;
  nameEn: string;
  namePt: string;
  descriptionEn: string;
  descriptionPt: string;
  criteriaType: string;
  criteriaValue: number;
  targetTag: string | null;
  /** Optional catalog grouping label (English). */
  themeEn?: string | null;
  /** Optional catalog grouping label (Portuguese). */
  themePt?: string | null;
  rarity: AchievementRarity | string;
  points: number;
  isSecret: boolean;
  /** Base name for `/assets/achievements/{key}.png` (optional). */
  iconAssetKey: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAchievementAdmin {
  nameEn: string;
  namePt: string;
  descriptionEn: string;
  descriptionPt: string;
  criteriaType: string;
  criteriaValue: number;
  targetTag?: string | null;
  themeEn?: string | null;
  themePt?: string | null;
  rarity: string;
  points: number;
  isSecret: boolean;
  iconAssetKey?: string | null;
}

export interface UpdateAchievementAdmin {
  nameEn: string;
  namePt: string;
  descriptionEn: string;
  descriptionPt: string;
  criteriaType: string;
  criteriaValue: number;
  targetTag?: string | null;
  themeEn?: string | null;
  themePt?: string | null;
  rarity: string;
  points: number;
  isSecret: boolean;
  iconAssetKey?: string | null;
  isActive: boolean;
}

export interface AchievementAdminFilters {
  rarity?: string;
  criteriaType?: string;
  isActive?: boolean;
}

/** Modo Carreira — admin (alinhado a CareerMissionRequirementTypes no backend). */
export type CareerMissionRequirementTypeApi =
  | 'PERFECT_WINS_COUNT'
  | 'SPECIFIC_QUESTIONS_CORRECT_ONCE'
  | 'QUIZ_PLAY_COUNT';

export interface CareerMissionQuestionGroupInput {
  label: string;
  questionIds: string[];
}

export interface CareerMissionRequirementInput {
  /** Preserve player progress: must be the server requirement id when editing an existing row. */
  id?: string;
  sortOrder: number;
  requirementType: CareerMissionRequirementTypeApi | string;
  targetCount?: number | null;
  questionIds?: string[];
  /** Multiple-choice source quiz: partition of questionIds (labels for UI). */
  questionGroups?: CareerMissionQuestionGroupInput[];
}

export interface CareerMissionQuestionGroupAdmin {
  label: string;
  questionIds: string[];
}

export interface CareerMissionRequirementAdmin {
  id: string;
  sortOrder: number;
  requirementType: string;
  targetCount?: number | null;
  questionIds: string[];
  questionGroups?: CareerMissionQuestionGroupAdmin[];
}

export interface CareerMissionAdmin {
  id: string;
  title: string;
  description?: string | null;
  titleEn?: string | null;
  descriptionEn?: string | null;
  sourceQuizId: string;
  sourceQuizTitle?: string | null;
  sourceQuizTitleEn?: string | null;
  rewardUnlockQuizId?: string | null;
  rewardUnlockQuizTitle?: string | null;
  rewardUnlockQuizTitleEn?: string | null;
  rewardXp: number;
  rewardAchievementId?: string | null;
  rewardAchievementNamePt?: string | null;
  rewardUnlockPlayMedium?: boolean;
  rewardUnlockPlayHard?: boolean;
  isActive: boolean;
  sortOrder: number;
  requirements: CareerMissionRequirementAdmin[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateCareerMissionAdmin {
  title: string;
  description?: string | null;
  titleEn: string;
  descriptionEn?: string | null;
  sourceQuizId: string;
  rewardUnlockQuizId?: string | null;
  rewardXp: number;
  rewardAchievementId?: string | null;
  rewardUnlockPlayMedium?: boolean;
  rewardUnlockPlayHard?: boolean;
  isActive: boolean;
  sortOrder: number;
  requirements: CareerMissionRequirementInput[];
}

export interface UpdateCareerMissionAdmin {
  title: string;
  description?: string | null;
  titleEn: string;
  descriptionEn?: string | null;
  sourceQuizId: string;
  rewardUnlockQuizId?: string | null;
  rewardXp: number;
  rewardAchievementId?: string | null;
  rewardUnlockPlayMedium?: boolean;
  rewardUnlockPlayHard?: boolean;
  isActive: boolean;
  sortOrder: number;
  requirements: CareerMissionRequirementInput[];
}

/** Missões diárias (jogador). */
export interface DailyMissionRewardPreview {
  rewardType: string;
  xpAmount?: number | null;
  itemTypeKey?: string | null;
  itemQuantity: number;
}

export interface DailyMissionTodayItem {
  kind: string;
  targetCount: number;
  progress: number;
  completed: boolean;
  rewards: DailyMissionRewardPreview[];
}

export interface DailyMissionsToday {
  periodStartUtc: string;
  missions: DailyMissionTodayItem[];
}

/** Ofensiva (dias consecutivos com pelo menos um quiz ou PvP, UTC). */
export interface LoginStreakDayUi {
  dayOfWeek: number;
  completed: boolean;
  isToday: boolean;
}

export interface LoginStreakHome {
  currentStreak: number;
  weekDays: LoginStreakDayUi[];
}

/** Missões diárias — admin. */
export interface DailyMissionRewardAdmin {
  rewardType: string;
  xpAmount?: number | null;
  itemTypeKey?: string | null;
  itemQuantity: number;
}

export interface DailyMissionAdminItem {
  kind: string;
  targetCount: number;
  rewards: DailyMissionRewardAdmin[];
}

export interface DailyMissionsAdminBundle {
  missions: DailyMissionAdminItem[];
}

export interface UpdateDailyMissionRewardAdmin {
  rewardType: string;
  xpAmount?: number | null;
  itemTypeKey?: string | null;
  itemQuantity?: number;
}

export interface UpdateDailyMissionAdminItem {
  kind: string;
  targetCount: number;
  rewards: UpdateDailyMissionRewardAdmin[];
}

export interface UpdateDailyMissionsAdmin {
  missions: UpdateDailyMissionAdminItem[];
}

/** Grupo MC no catálogo da missão (pré-visualização). */
export interface CareerMissionRequirementQuestionGroupCatalog {
  label: string;
  /** Answer previews for lobby listing (language from Accept-Language / server default). */
  answerLabels: string[];
}

export interface CareerMissionRequirementPublic {
  sortOrder: number;
  requirementType: string;
  targetCount?: number | null;
  specificQuestionCount: number;
  /** WITC / MC sem grupos: lista plana de textos de resposta (ordem de exibição). */
  specificQuestionAnswerPreviews?: string[] | null;
  /** MC com agrupamentos admin: grupos com rótulos + respostas. */
  questionGroupsCatalog?: CareerMissionRequirementQuestionGroupCatalog[] | null;
}

export interface CareerMissionPublic {
  id: string;
  title: string;
  description?: string | null;
  titleEn?: string | null;
  descriptionEn?: string | null;
  sourceQuizId: string;
  sourceQuizTitle?: string | null;
  sourceQuizTitleEn?: string | null;
  sourceQuizTheme?: string | null;
  sourceQuizThemeEn?: string | null;
  rewardUnlockQuizId?: string | null;
  rewardUnlockQuizTitle?: string | null;
  rewardUnlockQuizTitleEn?: string | null;
  rewardUnlockQuizTheme?: string | null;
  rewardUnlockQuizThemeEn?: string | null;
  rewardXp: number;
  rewardAchievementId?: string | null;
  rewardAchievementNamePt?: string | null;
  rewardAchievementNameEn?: string | null;
  rewardUnlockPlayMedium?: boolean;
  rewardUnlockPlayHard?: boolean;
  sortOrder: number;
  /** 0 = MultipleChoice, 1 = Witc — mesmo valor que no backend. */
  sourceQuizFormat?: number;
  requirements: CareerMissionRequirementPublic[];
}

/** GET /api/career/me — estado do jogador + catálogo (autenticado). */
export interface CareerSpecificQuestionSlot {
  questionId: string;
  satisfied: boolean;
  /** Only when satisfied; omitted in JSON for unanswered slots. */
  revealedAnswer?: string | null;
}

export interface CareerSpecificQuestionGroup {
  label: string;
  slots: CareerSpecificQuestionSlot[];
}

export interface CareerRequirementProgress {
  requirementId: string;
  requirementType: string;
  targetCount?: number | null;
  currentCount: number;
  specificQuestionCount: number;
  specificSatisfiedCount: number;
  /** Ordered by question ImageAssetKey as integer when parseable; only for SPECIFIC_QUESTIONS_CORRECT_ONCE when mission accepted/completed. */
  specificQuestions?: CareerSpecificQuestionSlot[];
  /** MC missions with admin-defined groups — prefer over flat specificQuestions when present. */
  specificQuestionGroups?: CareerSpecificQuestionGroup[];
}

export type CareerMissionPlayerStatus = 'none' | 'accepted' | 'paused' | 'completed';

export interface CareerMissionPlayerState {
  missionId: string;
  playerStatus: CareerMissionPlayerStatus;
  acceptedAt?: string | null;
  completedAt?: string | null;
  requirements: CareerRequirementProgress[];
}

export interface CareerMissionCatalogItem {
  mission: CareerMissionPublic;
  player: CareerMissionPlayerState;
}

export interface CareerHome {
  activeMissionId?: string | null;
  missions: CareerMissionCatalogItem[];
}

export interface UnlockedAchievementPayload {
  id: string;
  name: string;
  description?: string | null;
  rarity: string;
  soundKey?: string | null;
  iconAssetKey?: string | null;
  points: number;
  /** ISO 8601 UTC from API when the achievement was unlocked on this attempt. */
  unlockedAt?: string;
}

/** In-quiz character unlock: dependent question crossed prerequisite threshold on this attempt. */
export interface UnlockedCharacterPayload {
  unlockableQuestionId: string;
  imageAssetKey?: string | null;
  imageAssetsBasePath?: string | null;
}

export interface QuizAttemptResponse {
  success: boolean;
  message: string;
  earnedXP?: number;
  newLevel?: number;
  totalXP?: number;
  baseXP?: number;
  bonusXPPerCorrectAnswer?: number;
  totalBonusXP?: number;
  difficulty?: string;
  correctAnswers?: number;
  tooManySkips?: boolean;
  /** Server: true when solo quiz XP included +15% PWA bonus (header validated). */
  soloAppXpBonusApplied?: boolean;
  soloAppXpBonusPercent?: number;
  soloXpBeforeAppBonus?: number;
  soloAppXpBonusAmount?: number;
  /** Extra XP from answering quickly (solo linear speed bonus). */
  speedBonusXp?: number | null;
  /** Extra XP from VIP doubling the core solo formula (VIP subscriber only; server). */
  soloVipXpBonusAmount?: number | null;
  /** Extra XP from active inventory XP potion (solo; server). */
  soloXpPotionBonusAmount?: number | null;
  /** Potion buff percent when soloXpPotionBonusAmount > 0 (e.g. 30, 70, 100). */
  soloXpPotionBonusPercent?: number | null;
  /** PvP Arena × multiplier on post-VIP core (async host / results parity with solo-style breakdown). */
  pvpArenaExtraXp?: number | null;
  /** XP from solo formula after VIP double, before PvP arena multiplier (informational). */
  soloXpAfterVipBeforePvpMultiplier?: number | null;
  unlockedAchievements?: UnlockedAchievementPayload[];
  unlockedCharacters?: UnlockedCharacterPayload[];
  /** Present when this attempt recorded the first qualifying solo play for the current UTC day (same shape as GET login-streak/home). */
  loginStreakUpdated?: LoginStreakHome | null;
}

/** Maps live-room participant DTO (async host publish / lobby refresh) to solo-style {@link QuizAttemptResponse} for {@link SoloQuizResultsBody}. */
export function mapPvpParticipantToQuizAttemptResponse(p: PvPParticipantDto): QuizAttemptResponse {
  const credited = p.mainPhaseXpCredited ?? p.earnedXP ?? 0;
  const appAmt = p.pvpAppXpBonusAmount ?? 0;
  return {
    success: true,
    message: '',
    earnedXP: credited,
    baseXP: p.baseXp,
    bonusXPPerCorrectAnswer: p.bonusXpPerCorrectAnswer,
    totalBonusXP: p.totalBonusXp,
    difficulty: p.playDifficulty,
    correctAnswers: p.correctAnswers,
    speedBonusXp: p.speedBonusXp,
    soloVipXpBonusAmount: p.soloVipXpBonusAmount,
    soloXpPotionBonusAmount: p.soloXpPotionBonusAmount,
    soloXpPotionBonusPercent: p.soloXpPotionBonusPercent ?? undefined,
    soloAppXpBonusApplied: appAmt > 0,
    soloAppXpBonusPercent: p.pvpAppXpBonusPercent ?? undefined,
    soloAppXpBonusAmount: appAmt > 0 ? appAmt : undefined,
    pvpArenaExtraXp: p.pvpArenaExtraXp,
    soloXpAfterVipBeforePvpMultiplier: p.soloXpAfterVipBeforePvpMultiplier,
  };
}

/** S3-01 */
export interface AchievementRankingEntry {
  rank: number;
  userId: string;
  nickname: string;
  achievementScore: number;
  level: number;
  isVip?: boolean;
}

export interface AchievementRankingPage {
  items: AchievementRankingEntry[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

/** Public catalog (active achievements, paginated). */
export interface AchievementCatalogEntry {
  achievementId: string;
  name: string;
  description?: string | null;
  /** Localized catalog theme; omitted for secrets or when unset. */
  theme?: string | null;
  /** Set for non-secret rows; used for catalog hints (e.g. tag criteria). */
  criteriaType?: string | null;
  criteriaValue?: number | null;
  rarity: string;
  points: number;
  iconAssetKey?: string | null;
  isSecret: boolean;
}

export interface AchievementCatalogPage {
  items: AchievementCatalogEntry[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

/** S3-02 */
export interface MyAchievementEntry {
  achievementId: string;
  name: string;
  description?: string | null;
  rarity: string;
  points: number;
  iconAssetKey?: string | null;
  isSecret: boolean;
  unlockedAt: string;
}

/** S3-03 */
export interface PublicAchievementRecent {
  achievementId: string;
  name: string;
  description?: string | null;
  rarity: string;
  points: number;
  iconAssetKey?: string | null;
  unlockedAt: string;
}

export interface PublicUnlockedTotal {
  totalUnlocked: number;
}

/** S3-04 */
export interface SecretAchievementCounts {
  discovered: number;
  totalSecretAchievements: number;
}

export interface RankingEntry {
  rank: number;
  id: string;
  nickname: string;
  level: number;
  experiencePoints: number;
  isVip?: boolean;
}

export interface RankingResponse {
  ranking: RankingEntry[];
}

export interface XpLeaderboardEntry {
  rank: number;
  id: string;
  nickname: string;
  level: number;
  experiencePoints: number;
  soloQuizzesCompleted: number;
  pvpMatchesCompleted: number;
  achievementsUnlocked: number;
  /** Sum of points from unlocked achievements (cached on user). */
  achievementScore: number;
  /** VIP subscriber — highlighted in UI. */
  isVip?: boolean;
}

export interface XpLeaderboardPage {
  items: XpLeaderboardEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface XpLeaderboardFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  language?: string;
  minSoloCompleted?: number;
  minPvpCompleted?: number;
  minAchievementsUnlocked?: number;
  /** level | achievement | pvp | solo — API default level */
  sort?: 'level' | 'achievement' | 'pvp' | 'solo';
}

export interface PublicUserProfile {
  id: string;
  nickname: string;
  level: number;
  experiencePoints: number;
  /** API uses "Vip"; "Premium" may appear on legacy sessions. */
  userType: 'Free' | 'Vip' | 'Admin' | 'Premium' | 1 | 2 | 3;
}

export interface Friend {
  id: string;
  name: string;
  nickname: string;
  level: number;
  experiencePoints: number;
}

export interface PendingFriendship {
  id: number;
  requesterId: string;
  requesterName: string;
  requesterNickname: string;
  requesterLevel: number;
  createdAt: string;
}

export interface Friendship {
  id: number;
  requesterId: string;
  requesterName: string;
  requesterNickname: string;
  requesterLevel: number;
  receiverId: string;
  receiverName: string;
  receiverNickname: string;
  receiverLevel: number;
  status: 'Pending' | 'Accepted' | 'Rejected';
  createdAt: string;
  updatedAt?: string;
}

export interface FriendshipStatus {
  friendshipId?: number;
  status: 'None' | 'Pending' | 'Accepted' | 'Rejected';
  requesterId: string;
  receiverId: string;
}

export interface NotificationItem {
  id: string;
  category: string;
  title: string;
  body: string | null;
  createdAt: string;
  readAt: string | null;
}

export interface NotificationListResponse {
  items: NotificationItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface NotificationPreferences {
  adminBroadcast: boolean;
  friendship: boolean;
  progression: boolean;
  achievements: boolean;
  lives: boolean;
  pvp: boolean;
}

export interface AdminBroadcastRequest {
  titlePt: string;
  bodyPt?: string | null;
  titleEn: string;
  bodyEn?: string | null;
}

export interface AdminBroadcastResponse {
  notificationsCreated: number;
  usersSkippedByPreference: number;
}

export const notificationService = {
  getUnreadCount: async (): Promise<{ unreadCount: number }> => {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  },

  getNotifications: async (params?: {
    take?: number;
    page?: number;
    pageSize?: number;
  }): Promise<NotificationListResponse> => {
    const query: Record<string, number> = {};
    if (params?.take != null) {
      query.take = params.take;
    } else {
      query.page = params?.page ?? 1;
      query.pageSize = params?.pageSize ?? 20;
    }
    const response = await api.get('/notifications', { params: query });
    return response.data;
  },

  markRead: async (id: string): Promise<void> => {
    await api.patch(`/notifications/${id}/read`);
  },

  markAllRead: async (): Promise<{ updatedCount?: number }> => {
    const response = await api.patch('/notifications/read-all');
    return response.data;
  },

  getPreferences: async (): Promise<NotificationPreferences> => {
    const response = await api.get('/user/notification-preferences');
    return response.data;
  },

  updatePreferences: async (prefs: NotificationPreferences): Promise<void> => {
    await api.put('/user/notification-preferences', prefs);
  },

  adminBroadcast: async (body: AdminBroadcastRequest): Promise<AdminBroadcastResponse> => {
    const response = await api.post('/admin/notifications/broadcast', body);
    return response.data;
  },
};

/** Unread count for navbar badge; same refresh cycle as profile (no dedicated polling). Returns 0 if not logged in or on error. */
export async function loadUnreadNotificationCount(user: User | null): Promise<number> {
  if (!user) return 0;
  try {
    const { unreadCount } = await notificationService.getUnreadCount();
    return unreadCount;
  } catch {
    return 0;
  }
}

export type ContactKind = 'suggestion' | 'bug';

export const contactService = {
  submit: async (payload: { kind: ContactKind; message: string }): Promise<void> => {
    await api.post('/contact', payload);
  },
};

export const authService = {
  // Google OAuth login
  googleLogin: () => {
    window.location.href = `${API_BASE_URL}/auth/google/login`;
  },

  // Validate token
  validateToken: async (): Promise<User> => {
    const response = await api.get('/auth/validate');
    return response.data.user;
  },

  /** Persists UI language (en | pt); same as Preferences — required on nickname onboarding. */
  updateLanguagePreference: async (language: string): Promise<void> => {
    await api.put('/user/language', { language });
  },

  // Update nickname
  updateNickname: async (nickname: string): Promise<User> => {
    const response = await api.put('/user/nickname', { nickname });
    
    // Store the new token if provided
    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
    }
    
    return response.data.user;
  },

  // Update name
  updateName: async (name: string): Promise<User> => {
    const response = await api.put('/user/name', { name });
    
    // Store the new token if provided
    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
    }
    
    return response.data.user;
  },

  // Get user profile
  getProfile: async (): Promise<User> => {
    const response = await api.get('/user/profile');
    return response.data.user;
  },

  updateQuizSounds: async (quizSoundsEnabled: boolean): Promise<User> => {
    const response = await api.put('/user/quiz-sounds', { quizSoundsEnabled });
    return response.data.user;
  },

  // Get experience info
  getExperienceInfo: async (): Promise<ExperienceInfo> => {
    const response = await api.get('/user/experience');
    return response.data;
  },

  // Get ranking
  getRanking: async (): Promise<RankingEntry[]> => {
    const response = await api.get('/user/ranking');
    return response.data.ranking;
  },

  /** Paginated XP leaderboard (public). */
  getXpLeaderboard: async (filters: XpLeaderboardFilters = {}): Promise<XpLeaderboardPage> => {
    const response = await api.get('/user/ranking/leaderboard', {
      params: {
        page: filters.page ?? 1,
        pageSize: filters.pageSize ?? 10,
        search: filters.search || undefined,
        language: filters.language || undefined,
        minSoloCompleted: filters.minSoloCompleted ?? 0,
        minPvpCompleted: filters.minPvpCompleted ?? 0,
        minAchievementsUnlocked: filters.minAchievementsUnlocked ?? 0,
        sort: filters.sort ?? 'level',
      },
    });
    return response.data;
  },

  // Get public user profile
  getPublicProfile: async (userId: string): Promise<PublicUserProfile> => {
    const response = await api.get(`/user/profile/public/${userId}`);
    return response.data.user;
  },

  // Get lives (LivesStatus §5.2)
  getLives: async (): Promise<LivesStatus> => {
    const response = await api.get('/user/lives');
    return response.data;
  },
};

export const dailyMissionService = {
  getToday: async (): Promise<DailyMissionsToday> => {
    const response = await api.get('/daily-missions/today');
    return response.data;
  },
};

export const loginStreakService = {
  getHome: async (): Promise<LoginStreakHome> => {
    const response = await api.get('/login-streak');
    return response.data;
  },
};

export const friendshipService = {
  // Send friendship request
  sendFriendshipRequest: async (receiverId: string): Promise<Friendship> => {
    const response = await api.post('/friendship/send-request', { receiverId });
    return response.data;
  },

  // Respond to friendship request
  respondToFriendshipRequest: async (friendshipId: number, accept: boolean): Promise<Friendship> => {
    const response = await api.post('/friendship/respond-request', { friendshipId, accept });
    return response.data;
  },

  // Get friends list
  getFriendsList: async (): Promise<Friend[]> => {
    const response = await api.get('/friendship/friends');
    return response.data;
  },

  // Get pending friendship requests
  getPendingRequests: async (): Promise<PendingFriendship[]> => {
    const response = await api.get('/friendship/pending-requests');
    return response.data;
  },

  // Check if two users are friends
  areFriends: async (userId: string): Promise<boolean> => {
    const response = await api.get(`/friendship/are-friends/${userId}`);
    return response.data;
  },

  // Get friends count
  getFriendsCount: async (): Promise<number> => {
    const response = await api.get('/friendship/friends-count');
    return response.data;
  },

  // Remove friend
  removeFriend: async (friendId: string): Promise<void> => {
    await api.delete(`/friendship/remove-friend/${friendId}`);
  },

  // Get friendship status
  getFriendshipStatus: async (targetUserId: string): Promise<FriendshipStatus> => {
    const response = await api.get(`/friendship/status/${targetUserId}`);
    return response.data;
  },
};

export const adminService = {
  // Dashboard
  getDashboard: async (): Promise<AdminDashboard> => {
    const response = await api.get('/admin/dashboard');
    return response.data.dashboard;
  },

  getMostPlayedQuizzes: async (top?: number): Promise<MostPlayedQuizzesReportResponse> => {
    const searchParams = new URLSearchParams();
    if (top != null) searchParams.set('top', String(top));
    const q = searchParams.toString();
    const response = await api.get(`/admin/reports/most-played-quizzes${q ? `?${q}` : ''}`);
    const d = response.data as Record<string, unknown>;
    const rawList = d.items ?? d.Items;
    const mapRow = (x: Record<string, unknown>): MostPlayedQuizReportItem => {
      const themeRaw = x.theme ?? x.Theme;
      const themeStr = themeRaw != null && String(themeRaw).trim() !== '' ? String(themeRaw).trim() : null;
      return {
        quizId: String(x.quizId ?? x.QuizId ?? ''),
        title: String(x.title ?? x.Title ?? ''),
        theme: themeStr,
        playCount: Number(x.playCount ?? x.PlayCount ?? 0),
      };
    };
    return {
      items: Array.isArray(rawList)
        ? (rawList as Record<string, unknown>[]).map(mapRow)
        : [],
    };
  },

  /** Admin "Cadastros" report: new non-admin users bucketed for 24h / 7d / 30d windows. */
  getUserSignupsReport: async (): Promise<UserSignupsReportResponse> => {
    const response = await api.get('/admin/reports/user-signups');
    const d = response.data as Record<string, unknown>;

    const mapBucket = (x: Record<string, unknown>): UserSignupsReportBucket => ({
      bucketStartUtc: String(x.bucketStartUtc ?? x.BucketStartUtc ?? ''),
      count: Number(x.count ?? x.Count ?? 0),
    });

    const mapSeries = (raw: unknown): UserSignupsReportSeries => {
      const r = (raw ?? {}) as Record<string, unknown>;
      const granularityRaw = String(r.granularity ?? r.Granularity ?? 'day').toLowerCase();
      const granularity: 'hour' | 'day' = granularityRaw === 'hour' ? 'hour' : 'day';
      const buckets = r.buckets ?? r.Buckets;
      return {
        granularity,
        fromUtc: String(r.fromUtc ?? r.FromUtc ?? ''),
        toUtc: String(r.toUtc ?? r.ToUtc ?? ''),
        total: Number(r.total ?? r.Total ?? 0),
        buckets: Array.isArray(buckets)
          ? (buckets as Record<string, unknown>[]).map(mapBucket)
          : [],
      };
    };

    return {
      last24Hours: mapSeries(d.last24Hours ?? d.Last24Hours),
      last7Days: mapSeries(d.last7Days ?? d.Last7Days),
      last30Days: mapSeries(d.last30Days ?? d.Last30Days),
    };
  },

  // User management
  getUsers: async (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
  }): Promise<AdminUserListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.page != null) searchParams.set('page', String(params.page));
    if (params?.pageSize != null) searchParams.set('pageSize', String(params.pageSize));
    if (params?.search?.trim()) searchParams.set('search', params.search.trim());
    const q = searchParams.toString();
    const response = await api.get(`/admin/users${q ? `?${q}` : ''}`);
    return response.data;
  },

  promoteToAdmin: async (userId: string): Promise<void> => {
    await api.post(`/user/promote-admin/${userId}`);
  },

  demoteFromAdmin: async (userId: string): Promise<void> => {
    await api.post(`/user/demote-admin/${userId}`);
  },

  /** Admin tool: grant or extend VIP for a user by `days` days (stacks on remaining VIP). */
  grantVipDays: async (userId: string, days: number): Promise<void> => {
    await api.post(`/user/grant-vip/${userId}`, { days });
  },

  toggleUserStatus: async (userId: string): Promise<void> => {
    await api.post(`/admin/users/${userId}/toggle-status`);
  },

  // Quiz management (server-side pagination + search)
  getQuizzes: async (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
  }): Promise<QuizListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.page != null) searchParams.set('page', String(params.page));
    if (params?.pageSize != null) searchParams.set('pageSize', String(params.pageSize));
    if (params?.search?.trim()) searchParams.set('search', params.search.trim());
    const q = searchParams.toString();
    const response = await api.get(`/admin/quizzes${q ? `?${q}` : ''}`);
    return response.data;
  },

  /** Lightweight quiz list for admin dropdowns (titles only). */
  getQuizCompacts: async (): Promise<QuizCompactAdminDto[]> => {
    const response = await api.get('/admin/quizzes/compact');
    return response.data;
  },

  createQuiz: async (quizData: CreateQuiz): Promise<Quiz> => {
    const response = await api.post('/admin/quizzes', quizData);
    return response.data;
  },

  getQuizById: async (quizId: string): Promise<Quiz> => {
    const response = await api.get(`/admin/quizzes/${quizId}`);
    return response.data;
  },

  updateQuiz: async (quizId: string, quizData: UpdateQuiz): Promise<Quiz> => {
    const response = await api.put(`/admin/quizzes/${quizId}`, quizData);
    return response.data;
  },

  setQuizRecommendedSolo: async (quizId: string, recommendedSolo: boolean): Promise<Quiz> => {
    const response = await api.patch(`/admin/quizzes/${quizId}/recommended-solo`, {
      recommendedSolo,
    });
    return response.data;
  },

  deleteQuiz: async (quizId: string): Promise<void> => {
    await api.delete(`/admin/quizzes/${quizId}`);
  },

  activateQuiz: async (quizId: string): Promise<void> => {
    await api.post(`/admin/quizzes/${quizId}/activate`);
  },

  deactivateQuiz: async (quizId: string): Promise<void> => {
    await api.post(`/admin/quizzes/${quizId}/deactivate`);
  },

  setToDraft: async (quizId: string): Promise<void> => {
    await api.post(`/admin/quizzes/${quizId}/set-to-draft`);
  },

  // Question management (server-side pagination + search)
  getQuizQuestions: async (
    quizId: string,
    params?: { page?: number; pageSize?: number; search?: string }
  ): Promise<AdminQuestionListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.page != null) searchParams.set('page', String(params.page));
    if (params?.pageSize != null) searchParams.set('pageSize', String(params.pageSize));
    if (params?.search?.trim()) searchParams.set('search', params.search.trim());
    const q = searchParams.toString();
    const response = await api.get(`/admin/quizzes/${quizId}/questions${q ? `?${q}` : ''}`);
    return response.data;
  },

  createQuestion: async (questionData: CreateQuestion): Promise<Question> => {
    const response = await api.post('/admin/quizzes/questions', questionData);
    return response.data;
  },

  updateQuestion: async (questionId: string, questionData: UpdateQuestion): Promise<Question> => {
    const response = await api.put(`/admin/quizzes/questions/${questionId}`, questionData);
    return response.data;
  },

  deleteQuestion: async (questionId: string): Promise<void> => {
    await api.delete(`/admin/quizzes/questions/${questionId}`);
  },

  deleteAllQuizQuestions: async (quizId: string): Promise<void> => {
    await api.delete(`/admin/quizzes/${quizId}/questions`);
  },

  // JSON import methods
  validateQuestionsJson: async (quizId: string, jsonData: string): Promise<any> => {
    const response = await api.post(`/admin/quizzes/${quizId}/questions/validate-json`, { jsonData });
    return response.data;
  },

  importQuestionsFromJson: async (quizId: string, jsonData: string): Promise<any> => {
    const response = await api.post(`/admin/quizzes/${quizId}/questions/import-json`, { jsonData });
    return response.data;
  },

  getAchievements: async (
    filters?: AchievementAdminFilters & { page?: number; pageSize?: number }
  ): Promise<AdminAchievementListResponse> => {
    const params = new URLSearchParams();
    if (filters?.rarity) params.append('rarity', filters.rarity);
    if (filters?.criteriaType) params.append('criteriaType', filters.criteriaType);
    if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
    if (filters?.page != null) params.append('page', String(filters.page));
    if (filters?.pageSize != null) params.append('pageSize', String(filters.pageSize));
    const q = params.toString();
    const response = await api.get(`/admin/achievements${q ? `?${q}` : ''}`);
    const d = response.data as Record<string, unknown>;
    const rawList = d.achievements ?? d.Achievements;
    const achievements = Array.isArray(rawList) ? rawList : [];
    return {
      achievements,
      totalCount: Number(d.totalCount ?? d.TotalCount ?? 0),
      page: Number(d.page ?? d.Page ?? 1),
      pageSize: Number(d.pageSize ?? d.PageSize ?? 50),
      totalPages: Number(d.totalPages ?? d.TotalPages ?? 0),
      hasNextPage: Boolean(d.hasNextPage ?? d.HasNextPage ?? false),
      hasPreviousPage: Boolean(d.hasPreviousPage ?? d.HasPreviousPage ?? false),
    };
  },

  getAchievement: async (id: string): Promise<AdminAchievement> => {
    const response = await api.get(`/admin/achievements/${id}`);
    return response.data;
  },

  createAchievement: async (data: CreateAchievementAdmin): Promise<AdminAchievement> => {
    const response = await api.post('/admin/achievements', data);
    return response.data;
  },

  updateAchievement: async (id: string, data: UpdateAchievementAdmin): Promise<AdminAchievement> => {
    const response = await api.put(`/admin/achievements/${id}`, data);
    return response.data;
  },

  deactivateAchievement: async (id: string): Promise<AdminAchievement> => {
    const response = await api.post(`/admin/achievements/${id}/deactivate`);
    return response.data;
  },

  activateAchievement: async (id: string): Promise<AdminAchievement> => {
    const response = await api.post(`/admin/achievements/${id}/activate`);
    return response.data;
  },

  validateAchievementsJson: async (
    jsonData: string
  ): Promise<{
    message: string;
    achievementCount: number;
    achievements: CreateAchievementAdmin[];
  }> => {
    const response = await api.post('/admin/achievements/validate-json', { jsonData });
    return response.data;
  },

  importAchievementsFromJson: async (
    jsonData: string
  ): Promise<{
    message: string;
    importedCount: number;
    achievements: AdminAchievement[];
  }> => {
    const response = await api.post('/admin/achievements/import-json', { jsonData });
    return response.data;
  },

  getCareerMissions: async (params?: {
    page?: number;
    pageSize?: number;
  }): Promise<AdminCareerMissionListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.page != null) searchParams.set('page', String(params.page));
    if (params?.pageSize != null) searchParams.set('pageSize', String(params.pageSize));
    const q = searchParams.toString();
    const response = await api.get(`/admin/career-missions${q ? `?${q}` : ''}`);
    const d = response.data as Record<string, unknown>;
    const rawList = d.missions ?? d.Missions;
    const missions = Array.isArray(rawList) ? rawList : [];
    return {
      missions,
      totalCount: Number(d.totalCount ?? d.TotalCount ?? 0),
      page: Number(d.page ?? d.Page ?? 1),
      pageSize: Number(d.pageSize ?? d.PageSize ?? 50),
      totalPages: Number(d.totalPages ?? d.TotalPages ?? 0),
      hasNextPage: Boolean(d.hasNextPage ?? d.HasNextPage ?? false),
      hasPreviousPage: Boolean(d.hasPreviousPage ?? d.HasPreviousPage ?? false),
    };
  },

  getCareerMission: async (id: string): Promise<CareerMissionAdmin> => {
    const response = await api.get(`/admin/career-missions/${id}`);
    return response.data;
  },

  createCareerMission: async (data: CreateCareerMissionAdmin): Promise<CareerMissionAdmin> => {
    const response = await api.post('/admin/career-missions', data);
    return response.data;
  },

  updateCareerMission: async (id: string, data: UpdateCareerMissionAdmin): Promise<CareerMissionAdmin> => {
    const response = await api.put(`/admin/career-missions/${id}`, data);
    return response.data;
  },

  deleteCareerMission: async (id: string): Promise<void> => {
    await api.delete(`/admin/career-missions/${id}`);
  },

  getDailyMissions: async (): Promise<DailyMissionsAdminBundle> => {
    const response = await api.get('/admin/daily-missions');
    return response.data;
  },

  updateDailyMissions: async (body: UpdateDailyMissionsAdmin): Promise<DailyMissionsAdminBundle> => {
    const response = await api.put('/admin/daily-missions', body);
    return response.data;
  },

  getVipPricing: async (): Promise<VipPricingAdmin> => {
    const response = await api.get('/admin/vip-pricing');
    return response.data;
  },

  updateVipPricing: async (body: {
    originalPriceBrl: number;
    promoPriceBrl: number;
    durationDays: number;
    asyncPvPLobbyMinJoinableRooms: number;
  }): Promise<VipPricingAdmin> => {
    const response = await api.put('/admin/vip-pricing', body);
    return response.data;
  },

  /** ZIP with images from a sample URL (admin); optional inclusive number range (default 1–151). */
  downloadPokemonImagesZip: async (params: {
    sampleUrl: string;
    startNumber?: number;
    endNumber?: number;
  }): Promise<Blob> => {
    const response = await api.post<Blob>(
      '/admin/scraper/pokemon-images',
      {
        sampleUrl: params.sampleUrl,
        ...(params.startNumber != null && { startNumber: params.startNumber }),
        ...(params.endNumber != null && { endNumber: params.endNumber }),
      },
      {
        responseType: 'blob',
        validateStatus: (s) => s === 200 || s === 400 || s === 401 || s === 403,
      }
    );
    if (response.status !== 200) {
      const text = await (response.data as unknown as Blob).text();
      let msg = text;
      try {
        const j = JSON.parse(text) as { message?: string };
        if (j.message) msg = j.message;
      } catch {
        /* use raw text */
      }
      throw new Error(msg || `Request failed (${response.status})`);
    }
    return response.data;
  },
};

/** GET /api/quiz accepts only pageSize 12 or 21 (grid layout). Use for multi-page fetches. */
export const QUIZ_CATALOG_ALLOWED_PAGE_SIZE = 21;

export const quizService = {
  // Get active quizzes with pagination and filters
  getActiveQuizzes: async (filters: QuizFilters): Promise<QuizListResponse> => {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
    if (filters.difficulty) params.append('difficulty', filters.difficulty);
    if (filters.search) params.append('search', filters.search);
    if (filters.language) params.append('language', filters.language);
    if (filters.allLanguages) params.append('allLanguages', filters.allLanguages.toString());

    const response = await api.get(`/quiz?${params.toString()}`);
    return response.data;
  },

  /**
   * Loads all pages of active quizzes. The API rejects pageSize outside {12, 21}.
   */
  getActiveQuizzesAllPages: async (
    filters: Omit<QuizFilters, 'page' | 'pageSize'>
  ): Promise<Quiz[]> => {
    const pageSize = QUIZ_CATALOG_ALLOWED_PAGE_SIZE;
    const quizzes: Quiz[] = [];
    const maxPages = 100;
    for (let page = 1; page <= maxPages; page += 1) {
      const res = await quizService.getActiveQuizzes({ ...filters, page, pageSize });
      quizzes.push(...res.quizzes);
      if (!res.hasNextPage || res.quizzes.length === 0) break;
    }
    return quizzes;
  },

  // Get active quiz by id
  getActiveQuiz: async (id: string): Promise<Quiz> => {
    const response = await api.get(`/quiz/${id}`);
    return response.data;
  },

  /** Public: prerequisite hints + image keys for unlockable question silhouettes (PvP lobby). */
  getCharacterUnlockRoster: async (quizId: string): Promise<CharacterUnlockRosterResponse> => {
    const response = await api.get(`/quiz/${quizId}/character-unlock-roster`);
    const d = response.data as Record<string, unknown>;
    const itemsRaw = (d.items ?? d.Items) as unknown[] | undefined;
    const items: CharacterUnlockRosterItem[] = Array.isArray(itemsRaw)
      ? itemsRaw.map((it) => {
          const x = it as Record<string, unknown>;
          const kindRaw = String(x.unlockKind ?? x.UnlockKind ?? 'chain').toLowerCase();
          const unlockKind: CharacterUnlockRosterItem['unlockKind'] =
            kindRaw === 'difficulty_band' ? 'difficulty_band' : 'chain';
          const reqSum = x.requirementSummaryPt ?? x.RequirementSummaryPt;
          return {
            unlockableQuestionId: String(x.unlockableQuestionId ?? x.UnlockableQuestionId ?? ''),
            imageAssetKey: (() => {
              const raw = x.imageAssetKey ?? x.ImageAssetKey;
              if (raw == null) return undefined;
              const s = String(raw).trim();
              return s || undefined;
            })(),
            unlockKind,
            requirementSummaryPt:
              reqSum == null || reqSum === ''
                ? undefined
                : String(reqSum).trim() || undefined,
            prerequisiteAnswerSummaryPt: String(
              x.prerequisiteAnswerSummaryPt ?? x.PrerequisiteAnswerSummaryPt ?? ''
            ),
            timesRequired: Number(x.timesRequired ?? x.TimesRequired ?? 1) || 1,
            isUnlockedForViewer: (() => {
              const u = x.isUnlockedForViewer ?? x.IsUnlockedForViewer;
              return u === true || u === 'true';
            })(),
          };
        })
      : [];
    return {
      characterUnlockEnabled: Boolean(d.characterUnlockEnabled ?? d.CharacterUnlockEnabled),
      quizFormat: String(d.quizFormat ?? d.QuizFormat ?? ''),
      imageAssetsBasePath: (() => {
        const raw = d.imageAssetsBasePath ?? d.ImageAssetsBasePath;
        if (raw == null || raw === '') return '';
        return String(raw).trim();
      })(),
      items,
    };
  },

  /** Solo: debits 1 life unit (Free), returns quiz + questions + LivesStatus. */
  startSoloQuiz: async (
    quizId: string,
    body?: { playDifficulty?: string }
  ): Promise<SoloQuizStartResponse> => {
    const response = await api.post(`/quiz/${quizId}/start`, body ?? {});
    return response.data;
  },

  /** Solo Partida rápida: 10 questions, round-robin across source quizzes (WITC+MC), one slot per quiz per pass; shuffled order. */
  startSoloQuickMatch: async (): Promise<SoloQuizStartResponse> => {
    const response = await api.post('/quiz/quick-match/start');
    return response.data;
  },

  startQuickMatchMcSlot: async (
    questionId: string,
    playDifficulty: string
  ): Promise<{ playSessionId: string; engineBootstrapCipher: string }> => {
    const response = await api.post('/quiz/quick-match/mc-slot', {
      questionId,
      playDifficulty: playDifficulty.trim(),
    });
    const d = response.data as Record<string, unknown>;
    return {
      playSessionId: String(d.playSessionId ?? d.PlaySessionId ?? ''),
      engineBootstrapCipher: String(d.engineBootstrapCipher ?? d.EngineBootstrapCipher ?? ''),
    };
  },

  startQuickMatchWitcSlot: async (
    questionId: string,
    playDifficulty: string
  ): Promise<{ playSessionId: string; engineBootstrapCipher: string }> => {
    const response = await api.post('/quiz/quick-match/witc-slot', {
      questionId,
      playDifficulty: playDifficulty.trim(),
    });
    const d = response.data as Record<string, unknown>;
    return {
      playSessionId: String(d.playSessionId ?? d.PlaySessionId ?? ''),
      engineBootstrapCipher: String(d.engineBootstrapCipher ?? d.EngineBootstrapCipher ?? ''),
    };
  },

  /** Use context: 'pvp' when loading questions for PvP (bypasses solo-only POST /start). */
  getQuizQuestions: async (
    quizId: string,
    options?: { context?: 'pvp'; pvpRoomId?: string }
  ): Promise<Question[]> => {
    const headers: Record<string, string> | undefined =
      options?.context === 'pvp'
        ? {
            'X-Quizter-Quiz-Context': 'pvp',
            ...(options?.pvpRoomId?.trim()
              ? { 'X-Quizter-Pvp-Room-Id': options.pvpRoomId.trim() }
              : {}),
          }
        : undefined;
    const response = await api.get(`/quiz/${quizId}/questions`, { headers });
    return response.data;
  },

  submitGeniusRound: async (
    quizId: string,
    body: { playSessionId: string; picksInOrder: string[] }
  ): Promise<{
    correct: boolean;
    gameOver: boolean;
    sessionComplete: boolean;
    pointsEarnedThisRound: number;
    totalScore: number;
    nextRoundIndex: number;
    expectedPickCount: number;
    totalRounds: number;
  }> => {
    const response = await api.post(`/quiz/${quizId}/genius/submit-round`, body);
    const d = response.data as Record<string, unknown>;
    const num = (v: unknown): number => {
      if (typeof v === 'number' && !Number.isNaN(v)) return v;
      if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
      return 0;
    };
    return {
      correct: Boolean(d.correct ?? d.Correct),
      gameOver: Boolean(d.gameOver ?? d.GameOver),
      sessionComplete: Boolean(d.sessionComplete ?? d.SessionComplete),
      pointsEarnedThisRound: num(d.pointsEarnedThisRound ?? d.PointsEarnedThisRound),
      totalScore: num(d.totalScore ?? d.TotalScore),
      nextRoundIndex: num(d.nextRoundIndex ?? d.NextRoundIndex),
      expectedPickCount: num(d.expectedPickCount ?? d.ExpectedPickCount),
      totalRounds: num(d.totalRounds ?? d.TotalRounds) || 10,
    };
  },



  /** MC play: GET questions omit correctness — validate each pick before advancing. */
  validateMcAnswer: async (
    quizId: string,
    questionId: string,
    body: { answerId: string; playDifficulty?: string | null }
  ): Promise<{ correct: boolean; correctAnswerId?: string | null }> => {
    const response = await api.post(
      `/quiz/${quizId}/questions/${questionId}/validate-mc`,
      {
        answerId: body.answerId,
        playDifficulty: body.playDifficulty?.trim() || undefined,
      }
    );
    return response.data;
  },

  validateStopAnswer: async (
    quizId: string,
    questionId: string,
    text: string,
    options?: { stopPlaySessionId?: string | null; pvpRoomId?: string | null }
  ): Promise<{
    correct: boolean;
    duplicate: boolean;
    foundCount: number;
    requiredCount: number;
    wrongCount: number;
    maxWrong: number;
    questionComplete: boolean;
    questionFailed: boolean;
    pointsEarned?: number | null;
    questionPointsSoFar: number;
    revealedAnswer?: {
      answerId: string;
      text: string;
      textEn?: string | null;
      imageAssetKey: string;
    } | null;
  }> => {
    const body: {
      questionId: string;
      text: string;
      witcPlaySessionId?: string;
      pvpRoomId?: string;
    } = {
      questionId,
      text,
    };
    if (options?.stopPlaySessionId) body.witcPlaySessionId = options.stopPlaySessionId;
    if (options?.pvpRoomId) body.pvpRoomId = options.pvpRoomId;
    const response = await api.post(`/quiz/${quizId}/stop/validate-answer`, body);
    const d = response.data as Record<string, unknown>;
    const revealedRaw = (d.revealedAnswer ?? d.RevealedAnswer) as Record<string, unknown> | null | undefined;
    const num = (v: unknown): number | undefined => {
      if (typeof v === 'number' && !Number.isNaN(v)) return v;
      if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
      return undefined;
    };
    const pointsEarnedRaw = d.pointsEarned ?? d.PointsEarned;
    return {
      correct: Boolean(d.correct ?? d.Correct),
      duplicate: Boolean(d.duplicate ?? d.Duplicate),
      foundCount: num(d.foundCount ?? d.FoundCount) ?? 0,
      requiredCount: num(d.requiredCount ?? d.RequiredCount) ?? 3,
      wrongCount: num(d.wrongCount ?? d.WrongCount) ?? 0,
      maxWrong: num(d.maxWrong ?? d.MaxWrong) ?? 3,
      questionComplete: Boolean(d.questionComplete ?? d.QuestionComplete),
      questionFailed: Boolean(d.questionFailed ?? d.QuestionFailed),
      pointsEarned:
        pointsEarnedRaw === null || pointsEarnedRaw === undefined
          ? undefined
          : num(pointsEarnedRaw) ?? null,
      questionPointsSoFar: num(d.questionPointsSoFar ?? d.QuestionPointsSoFar) ?? 0,
      revealedAnswer: revealedRaw
        ? {
            answerId: String(revealedRaw.answerId ?? revealedRaw.AnswerId ?? ''),
            text: String(revealedRaw.text ?? revealedRaw.Text ?? ''),
            textEn: (revealedRaw.textEn ?? revealedRaw.TextEn ?? null) as string | null,
            imageAssetKey: String(revealedRaw.imageAssetKey ?? revealedRaw.ImageAssetKey ?? ''),
          }
        : null,
    };
  },

  validateWitcAnswer: async (
    quizId: string,
    questionId: string,
    text: string,
    options?: { witcPlaySessionId?: string | null; pvpRoomId?: string | null }
  ): Promise<{
    correct: boolean;
    revealExpectedAnswer?: string | null;
    mediumHintLetters?: Array<{ index: number; char: string }>;
  }> => {
    const body: {
      questionId: string;
      text: string;
      witcPlaySessionId?: string;
      pvpRoomId?: string;
    } = { questionId, text };
    if (options?.witcPlaySessionId) body.witcPlaySessionId = options.witcPlaySessionId;
    if (options?.pvpRoomId) body.pvpRoomId = options.pvpRoomId;
    const response = await api.post(`/quiz/${quizId}/witc/validate-answer`, body);
    return response.data;
  },

  // Save quiz attempt
  saveQuizAttempt: async (
    attempt: {
      quizId: string;
      score: number;
      correctAnswers: number;
      totalQuestions: number;
      duration: number;
      isCompleted: boolean;
      answers: string[];
      /** Same order as answers — required for server-side grading and tag achievements. */
      questionIdsInOrder?: string[];
      /** Seconds spent per question (same order as answers). Used for speed bonus XP. */
      responseTimeSecondsPerQuestion?: number[];
      /** WITC quizzes — same order as questionIdsInOrder. */
      witcResults?: Array<{ pointsEarned: number; finalAnswerText?: string | null }>;
      /** Word Connect solo: signed placement log from WASM. */
      wordConnectFinishAudit?: WordConnectFinishAudit;
      /** Tile Match solo: signed action log from WASM. */
      tileMatchFinishAudit?: TileMatchFinishAudit;
      /** Witc solo: signed action log from WASM. */
      witcFinishAudit?: WitcFinishAudit;
      /** Stop solo: signed submission log from WASM. */
      stopFinishAudit?: StopFinishAudit;
      /** Genius solo: signed action log from WASM. */
      geniusFinishAudit?: GeniusFinishAudit;
      /** MC solo: signed action log from WASM. */
      mcFinishAudit?: McFinishAudit;
      /** Must match POST /quiz/{id}/start when the quiz has multiple play difficulties. */
      playDifficulty?: string;
      soloQuickMatch?: boolean;
      playDifficultiesPerQuestion?: string[];
    },
    options?: { pwaClient?: boolean }
  ): Promise<QuizAttemptResponse> => {
    const headers: Record<string, string> = {};
    if (options?.pwaClient) {
      headers['X-Quizter-Client'] = 'pwa';
    }
    const response = await api.post('/quiz/attempt', attempt, {
      headers: Object.keys(headers).length ? headers : undefined,
    });
    return response.data;
  },
};

export const achievementService = {
  getRanking: async (page = 1, pageSize = 20): Promise<AchievementRankingPage> => {
    const response = await api.get('/achievements/ranking', { params: { page, pageSize } });
    return response.data;
  },

  /** @param rarityOrder Default `asc` = Common→Rare→Epic; `desc` = most rare first (Epic→Rare→Common) */
  /** @param theme Optional substring (case-insensitive) on ThemeEn or ThemePt. */
  getCatalog: async (
    page = 1,
    pageSize = 24,
    rarityOrder: 'asc' | 'desc' = 'asc',
    unlockFilter: 'all' | 'obtained' | 'not_obtained' = 'all',
    theme?: string | null
  ): Promise<AchievementCatalogPage> => {
    const params: Record<string, string | number> = {
      page,
      pageSize,
      rarityOrder,
      unlockFilter,
    };
    const themeTrim = theme?.trim();
    if (themeTrim) params.theme = themeTrim;
    const response = await api.get('/achievements/catalog', { params });
    return response.data;
  },

  getMine: async (): Promise<MyAchievementEntry[]> => {
    const response = await api.get('/achievements/mine');
    return response.data;
  },

  getPublicRecent: async (userId: string, take = 5): Promise<PublicAchievementRecent[]> => {
    const response = await api.get(`/achievements/public/${userId}/recent`, { params: { take } });
    return response.data;
  },

  getPublicUnlockedTotal: async (userId: string): Promise<PublicUnlockedTotal> => {
    const response = await api.get(`/achievements/public/${userId}/unlocked-total`);
    return response.data;
  },

  getPublicUnlocked: async (userId: string): Promise<MyAchievementEntry[]> => {
    const response = await api.get(`/achievements/public/${userId}/unlocked`);
    return response.data;
  },

  getMySecretCounts: async (): Promise<SecretAchievementCounts> => {
    const response = await api.get('/achievements/mine/secrets');
    return response.data;
  },
};

export interface InventoryXpBuffDto {
  bonusPercent: number;
  expiresAtUtc: string;
  /** Full minutes left, computed on the server from UTC. */
  minutesRemaining: number;
}

export interface InventoryItemDto {
  id: string;
  itemTypeKey: string;
  expiresAtUtc?: string | null;
}

export interface InventorySlotDto {
  slotIndex: number;
  item?: InventoryItemDto | null;
  /**
   * True for slots part of the VIP-only block when the user is not a VIP subscriber.
   * Render as locked with a "Unlock with VIP" call-to-action.
   */
  isVipLocked?: boolean;
}

export interface InventoryPageDto {
  /** Total slots rendered (always the VIP cap so locked slots are visible to non-VIP). */
  maxSlots: number;
  /** Slots available without VIP — slots with `slotIndex >= freeMaxSlots` require VIP. */
  freeMaxSlots: number;
  slots: InventorySlotDto[];
  activeXpBoost?: InventoryXpBuffDto | null;
}

export const inventoryService = {
  getMine: async (): Promise<InventoryPageDto> => {
    const response = await api.get('/inventory');
    return response.data;
  },

  useItem: async (itemId: string): Promise<void> => {
    await api.post(`/inventory/items/${itemId}/use`);
  },

  discardItem: async (itemId: string): Promise<void> => {
    await api.delete(`/inventory/items/${itemId}`);
  },
};

export interface PvPRoomFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  /** Easy | Medium | Hard — omit or empty string for all */
  difficulty?: string;
}

export interface PvPRoomListResponse {
  rooms: PvPRoom[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface PvPParticipantDto {
  id?: string;
  userId: string;
  nickname?: string;
  userName?: string;
  /** Present on API mapped participants */
  isHost?: boolean;
  score?: number;
  correctAnswers?: number;
  totalQuestions?: number;
  earnedXP?: number;
  winnerBonusXp?: number;
  completedAt?: string | null;
  isVip?: boolean;
  /** XP da fase principal após poção (headline do modal async host). */
  mainPhaseXpCredited?: number;
  pvpPreWinnerPerformanceXp?: number;
  baseXp?: number;
  bonusXpPerCorrectAnswer?: number;
  totalBonusXp?: number;
  playDifficulty?: string;
  soloVipXpBonusAmount?: number;
  soloXpAfterVipBeforePvpMultiplier?: number;
  pvpArenaExtraXp?: number;
  speedBonusXp?: number;
  pvpAppXpBonusAmount?: number;
  pvpAppXpBonusPercent?: number | null;
  soloXpPotionBonusAmount?: number;
  soloXpPotionBonusPercent?: number | null;
}

export interface PvpRoomInvitePreview {
  roomId: string;
  roomCode: string;
  name: string;
  quizTitle: string;
  quizTitleEn?: string | null;
  quizTheme?: string | null;
  quizThemeEn?: string | null;
  quizFormat?: string;
  quizDifficulty: string;
  hostNickname: string;
  status: string;
  hostIsVip?: boolean;
}

export interface PvPRoom {
  id: string;
  /** 5-character invite path segment for `/pvp-quiz/{roomCode}`. */
  roomCode?: string;
  name: string;
  type: string;
  status: string;
  hostId: string;
  hostName: string;
  hostNickname: string;
  /** Host is VIP — same highlight as leaderboard. */
  hostIsVip?: boolean;
  quizId: string;
  quizTitle: string;
  quizTitleEn?: string | null;
  quizTheme?: string | null;
  quizThemeEn?: string | null;
  quizFormat?: string;
  quizDifficulty: string;
  /** Quiz language code (e.g. en, pt) for lobby flag */
  quizLanguage?: string;
  /** Quiz marked as unlockable (career) — show padlock in lobby. */
  quizIsUnlockable?: boolean;
  /** In-quiz prerequisite unlock chain — show info modal in PvP lobby. */
  quizCharacterUnlockEnabled?: boolean;
  participantCount: number;
  maxParticipants: number;
  createdAt: string;
  startedAt?: string;
  endedAt?: string;
  publishedAt?: string | null;
  /** When status is Cancelled (e.g. HostQuizTooManySkips). */
  cancellationReason?: string;
  isUserHost: boolean;
  isUserInRoom: boolean;
  canStart: boolean;
  /** True when opponent finished with too many skips: room back in lobby, no XP, no life refund. */
  opponentQuizAbortedDueToSkips?: boolean;
  opponentUserId?: string | null;
  participants?: PvPParticipantDto[];
}

export interface PvPRoomResultParticipant {
  userId: string;
  nickname: string;
  userName?: string;
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  completionTime: string | number;
  rank: number;
  isHost: boolean;
  earnedXP: number;
  winnerBonusXp: number;
  newLevel: number;
  totalXP: number;
  isVip?: boolean;
  /** Solo-style breakdown from server. */
  baseXp?: number;
  bonusXpPerCorrectAnswer?: number;
  totalBonusXp?: number;
  soloVipXpBonusAmount?: number;
  soloXpAfterVipBeforePvpMultiplier?: number;
  pvpArenaExtraXp?: number;
  pvpAppXpBonusAmount?: number;
  pvpAppXpBonusPercent?: number | null;
  speedBonusXp?: number;
  /** Extra XP from inventory XP potion (PvP resolve; same semantics as solo). */
  soloXpPotionBonusAmount?: number;
  soloXpPotionBonusPercent?: number | null;
}

/** GET /quiz/{id}/character-unlock-roster — PvP lobby silhouettes. */
export interface CharacterUnlockRosterItem {
  unlockableQuestionId: string;
  imageAssetKey?: string | null;
  /** chain = prerequisite progression; difficulty_band = ExcludeFromEasy / mission unlock band */
  unlockKind?: 'chain' | 'difficulty_band';
  /** Full PT line when unlockKind is difficulty_band */
  requirementSummaryPt?: string | null;
  prerequisiteAnswerSummaryPt: string;
  timesRequired: number;
  /** Viewer met prerequisite count — lobby roster shows full colour art. */
  isUnlockedForViewer?: boolean;
}

export interface CharacterUnlockRosterResponse {
  characterUnlockEnabled: boolean;
  quizFormat: string;
  imageAssetsBasePath: string;
  items: CharacterUnlockRosterItem[];
}

export interface PvPResultUserCelebration {
  userId: string;
  unlockedAchievements?: unknown[];
  unlockedCharacters?: unknown[];
  loginStreakUpdated?: unknown;
}

export interface PvPRoomResultDto {
  roomId: string;
  roomName: string;
  quizTitle: string;
  quizTitleEn?: string | null;
  quizTheme?: string | null;
  quizThemeEn?: string | null;
  quizFormat?: string;
  quizDifficulty?: string;
  startedAt: string;
  endedAt: string;
  results: PvPRoomResultParticipant[];
  unlockedAchievementsByUser?: { userId: string; unlockedAchievements: unknown[] }[];
  /** Solo-parity celebration slices per user (achievements, character unlocks, login streak). */
  celebrationByUser?: PvPResultUserCelebration[];
}

/** Open Arena PvP rooms you host (Draft / AwaitingOpponent) — for lobby create limits. */
export interface HostedOpenAsyncPvPSummary {
  openCount: number;
  openQuizIds: string[];
  maxOpenRooms: number;
}

export const careerService = {
  /** Missões ativas cadastradas no admin (catálogo para o jogador). */
  listActiveMissions: async (): Promise<CareerMissionPublic[]> => {
    const response = await api.get<CareerMissionPublic[]>('/career/missions');
    return response.data;
  },

  /** Catálogo + progresso do utilizador (Bearer). */
  getCareerHome: async (): Promise<CareerHome> => {
    const response = await api.get<CareerHome>('/career/me');
    return response.data;
  },

  /** Aceita uma missão (Bearer). 204 sem corpo. */
  acceptMission: async (missionId: string): Promise<void> => {
    await api.post(`/career/missions/${missionId}/accept`);
  },

  pauseMission: async (missionId: string): Promise<void> => {
    await api.post(`/career/missions/${missionId}/pause`);
  },

  resumeMission: async (missionId: string): Promise<void> => {
    await api.post(`/career/missions/${missionId}/resume`);
  },
};

function parseTileMatchTilesFromApi(arr: unknown): TileMatchTileDto[] {
  if (!Array.isArray(arr)) return [];
  const out: TileMatchTileDto[] = [];
  for (const t of arr) {
    if (!t || typeof t !== 'object') continue;
    const o = t as Record<string, unknown>;
    const id = String(o.id ?? o.Id ?? '').trim();
    const imageAssetKey = String(o.imageAssetKey ?? o.ImageAssetKey ?? '').trim();
    if (!id || !imageAssetKey) continue;
    const num = (v: unknown) => {
      if (typeof v === 'number' && !Number.isNaN(v)) return v;
      if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
      return 0;
    };
    out.push({
      id,
      imageAssetKey,
      questionId:
        o.questionId != null
          ? String(o.questionId)
          : o.QuestionId != null
            ? String(o.QuestionId)
            : undefined,
      x: num(o.x ?? o.X),
      y: num(o.y ?? o.Y),
      z: num(o.z ?? o.Z),
      isBlocked: Boolean(o.isBlocked ?? o.IsBlocked),
    });
  }
  return out;
}

function parseTileMatchPlayStateFromApi(raw: unknown): TileMatchPlayState {
  const d = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const num = (v: unknown, fallback = 0): number => {
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
    return fallback;
  };
  const boardRaw = d.board ?? d.Board;
  const barRaw = d.selectionBar ?? d.SelectionBar;
  const holdRaw = d.evacuateHold ?? d.EvacuateHold;
  const nextRaw = d.nextRound ?? d.NextRound;
  const pointsPerRoundRaw = d.pointsPerRound ?? d.PointsPerRound;
  let pointsPerRound: number[] | undefined;
  if (Array.isArray(pointsPerRoundRaw)) {
    pointsPerRound = pointsPerRoundRaw.map((v: unknown) => num(v));
  }
  let nextRound: TileMatchRoundDto | undefined;
  if (nextRaw && typeof nextRaw === 'object') {
    const o = nextRaw as Record<string, unknown>;
    nextRound = {
      roundIndex: num(o.roundIndex ?? o.RoundIndex, 1),
      tiles: parseTileMatchTilesFromApi(o.tiles ?? o.Tiles),
      distinctKeyCount: num(o.distinctKeyCount ?? o.DistinctKeyCount),
      triosPerKey: num(o.triosPerKey ?? o.TriosPerKey),
      totalTiles: num(o.totalTiles ?? o.TotalTiles),
      maxLayers: num(o.maxLayers ?? o.MaxLayers),
    };
  }
  return {
    currentRound: num(d.currentRound ?? d.CurrentRound, 1),
    board: parseTileMatchTilesFromApi(boardRaw),
    selectionBar: parseTileMatchTilesFromApi(barRaw),
    evacuateHold: parseTileMatchTilesFromApi(holdRaw),
    isGameOver: Boolean(d.isGameOver ?? d.IsGameOver),
    isRoundWon: Boolean(d.isRoundWon ?? d.IsRoundWon),
    isRunWon: Boolean(d.isRunWon ?? d.IsRunWon),
    sessionComplete: Boolean(d.sessionComplete ?? d.SessionComplete),
    pointsEarnedThisRound: num(d.pointsEarnedThisRound ?? d.PointsEarnedThisRound),
    totalScore: num(d.totalScore ?? d.TotalScore),
    pointsPerRound,
    nextRound,
    shuffleUsed: Boolean(d.shuffleUsed ?? d.ShuffleUsed),
    undoUsed: Boolean(d.undoUsed ?? d.UndoUsed),
    evacuateUsed: Boolean(d.evacuateUsed ?? d.EvacuateUsed),
  };
}

function parseWordConnectPlayStateFromApi(raw: unknown): WordConnectPlayState {
  if (!raw || typeof raw !== 'object') {
    return {
      words: [],
      cells: [],
      letterPool: [],
      totalScore: 0,
      wordsCompleted: 0,
      activeWordCount: 0,
      sessionComplete: false,
      wordJustCompleted: false,
      pointsEarnedThisWord: 0,
      pointsPerWordSlot: [],
    };
  }
  const d = raw as Record<string, unknown>;
  const num = (v: unknown, fallback = 0): number => {
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
    return fallback;
  };
  const wordsRaw = d.words ?? d.Words;
  const words: WordConnectWordDto[] = Array.isArray(wordsRaw)
    ? wordsRaw
        .map((w): WordConnectWordDto | null => {
          if (!w || typeof w !== 'object') return null;
          const o = w as Record<string, unknown>;
          const questionId = String(o.questionId ?? o.QuestionId ?? '').trim();
          const text = String(o.text ?? o.Text ?? '').trim();
          const length = num(o.length ?? o.Length, text.length);
          if (!questionId || length < 1) return null;
          return {
            questionId,
            text: '',
            length,
            startRow: num(o.startRow ?? o.StartRow),
            startCol: num(o.startCol ?? o.StartCol),
            isHorizontal: Boolean(o.isHorizontal ?? o.IsHorizontal ?? true),
            isCompleted: Boolean(o.isCompleted ?? o.IsCompleted),
          };
        })
        .filter((w): w is WordConnectWordDto => w != null)
    : [];
  const cellsRaw = d.cells ?? d.Cells;
  const cells: WordConnectCellDto[] = Array.isArray(cellsRaw)
    ? cellsRaw
        .map((c): WordConnectCellDto | null => {
          if (!c || typeof c !== 'object') return null;
          const o = c as Record<string, unknown>;
          const wordColorRaw = o.wordColorIndex ?? o.WordColorIndex;
          return {
            row: num(o.row ?? o.Row),
            col: num(o.col ?? o.Col),
            letter: o.letter != null ? String(o.letter) : o.Letter != null ? String(o.Letter) : null,
            wordColorIndex:
              wordColorRaw === null || wordColorRaw === undefined ? null : num(wordColorRaw),
          };
        })
        .filter((c): c is WordConnectCellDto => c != null)
    : [];
  const poolRaw = d.letterPool ?? d.LetterPool;
  const letterPool: WordConnectLetterSlotDto[] = Array.isArray(poolRaw)
    ? poolRaw
        .map((s): WordConnectLetterSlotDto | null => {
          if (!s || typeof s !== 'object') return null;
          const o = s as Record<string, unknown>;
          const slotId = String(o.slotId ?? o.SlotId ?? '').trim();
          const letter = String(o.letter ?? o.Letter ?? '').trim();
          if (!slotId || !letter) return null;
          return {
            slotId,
            letter,
            sourceQuestionId: String(o.sourceQuestionId ?? o.SourceQuestionId ?? '').trim() || undefined,
            wordColorIndex: num(o.wordColorIndex ?? o.WordColorIndex),
          };
        })
        .filter((s): s is WordConnectLetterSlotDto => s != null)
    : [];
  const pointsPerWordSlotRaw = d.pointsPerWordSlot ?? d.PointsPerWordSlot;
  const pointsPerWordSlot = Array.isArray(pointsPerWordSlotRaw)
    ? pointsPerWordSlotRaw.map((v) => num(v))
    : [];
  return {
    words,
    cells,
    letterPool,
    totalScore: num(d.totalScore ?? d.TotalScore),
    wordsCompleted: num(d.wordsCompleted ?? d.WordsCompleted),
    activeWordCount: num(d.activeWordCount ?? d.ActiveWordCount),
    sessionComplete: Boolean(d.sessionComplete ?? d.SessionComplete),
    wordJustCompleted: Boolean(d.wordJustCompleted ?? d.WordJustCompleted),
    pointsEarnedThisWord: num(d.pointsEarnedThisWord ?? d.PointsEarnedThisWord),
    pointsPerWordSlot,
    placementAccepted: d.placementAccepted !== false && d.PlacementAccepted !== false,
    placementMessage:
      d.placementMessage != null
        ? String(d.placementMessage)
        : d.PlacementMessage != null
          ? String(d.PlacementMessage)
          : null,
  };
}

function parseWordConnectPlayStartFromApi(raw: unknown): WordConnectPlayStart | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const playSessionId = String(o.playSessionId ?? o.PlaySessionId ?? '').trim();
  if (!playSessionId) return null;

  const num = (v: unknown, fallback = 0): number => {
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
    return fallback;
  };

  const wordsRaw = o.words ?? o.Words;
  const words: WordConnectWordDto[] = Array.isArray(wordsRaw)
    ? wordsRaw
        .map((w): WordConnectWordDto | null => {
          if (!w || typeof w !== 'object') return null;
          const r = w as Record<string, unknown>;
          const questionId = String(r.questionId ?? r.QuestionId ?? '').trim();
          const text = String(r.text ?? r.Text ?? '').trim();
          const length = num(r.length ?? r.Length, text.length);
          if (!questionId || length < 1) return null;
          return {
            questionId,
            text: '',
            length,
            startRow: num(r.startRow ?? r.StartRow),
            startCol: num(r.startCol ?? r.StartCol),
            isHorizontal: Boolean(r.isHorizontal ?? r.IsHorizontal ?? true),
            isCompleted: Boolean(r.isCompleted ?? r.IsCompleted),
          };
        })
        .filter((w): w is WordConnectWordDto => w != null)
    : [];

  const cellsRaw = o.cells ?? o.Cells;
  const cells: WordConnectCellDto[] = Array.isArray(cellsRaw)
    ? cellsRaw
        .map((c): WordConnectCellDto | null => {
          if (!c || typeof c !== 'object') return null;
          const r = c as Record<string, unknown>;
          const wordColorRaw = r.wordColorIndex ?? r.WordColorIndex;
          return {
            row: num(r.row ?? r.Row),
            col: num(r.col ?? r.Col),
            letter: r.letter != null ? String(r.letter) : r.Letter != null ? String(r.Letter) : null,
            wordColorIndex:
              wordColorRaw === null || wordColorRaw === undefined ? null : num(wordColorRaw),
          };
        })
        .filter((c): c is WordConnectCellDto => c != null)
    : [];

  const poolRaw = o.letterPool ?? o.LetterPool;
  const letterPool: WordConnectLetterSlotDto[] = Array.isArray(poolRaw)
    ? poolRaw
        .map((s): WordConnectLetterSlotDto | null => {
          if (!s || typeof s !== 'object') return null;
          const r = s as Record<string, unknown>;
          const slotId = String(r.slotId ?? r.SlotId ?? '').trim();
          const letter = String(r.letter ?? r.Letter ?? '').trim();
          if (!slotId || !letter) return null;
          return {
            slotId,
            letter,
            sourceQuestionId: String(r.sourceQuestionId ?? r.SourceQuestionId ?? '').trim() || undefined,
            wordColorIndex: num(r.wordColorIndex ?? r.WordColorIndex),
          };
        })
        .filter((s): s is WordConnectLetterSlotDto => s != null)
    : [];

  const activeQuestionIdsRaw = o.activeQuestionIds ?? o.ActiveQuestionIds;
  const activeQuestionIds = Array.isArray(activeQuestionIdsRaw)
    ? activeQuestionIdsRaw.map((id) => String(id))
    : undefined;

  return {
    playSessionId,
    activeQuestionIds,
    activeWordCount: num(o.activeWordCount ?? o.ActiveWordCount, words.length),
    initialTimerSeconds: num(o.initialTimerSeconds ?? o.InitialTimerSeconds, 60),
    timeBonusPerLetterSeconds: num(o.timeBonusPerLetterSeconds ?? o.TimeBonusPerLetterSeconds, 3),
    timeBonusPerWordSeconds: num(o.timeBonusPerWordSeconds ?? o.TimeBonusPerWordSeconds, 10),
    pointsPerWord: num(o.pointsPerWord ?? o.PointsPerWord, 6),
    engineBootstrapCipher: (o.engineBootstrapCipher ?? o.EngineBootstrapCipher ?? undefined) as string | undefined,
    words,
    cells,
    letterPool,
  };
}

function parseGeniusPlayStartFromApi(raw: unknown): GeniusPlayStart | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  const panelRaw = (d.panel ?? d.Panel) as unknown;
  if (!Array.isArray(panelRaw) || panelRaw.length === 0) return null;
  const panel = panelRaw.map((p) => {
    const row = p as Record<string, unknown>;
    return {
      questionId: String(row.questionId ?? row.QuestionId ?? ''),
      imageAssetKey: String(row.imageAssetKey ?? row.ImageAssetKey ?? ''),
      soundAssetKey: String(row.soundAssetKey ?? row.SoundAssetKey ?? ''),
      label: (row.label ?? row.Label ?? null) as string | null,
    };
  });
  const roundsRaw = (d.rounds ?? d.Rounds) as unknown;
  const rounds = Array.isArray(roundsRaw)
    ? roundsRaw.map((r) => (Array.isArray(r) ? r.map((id) => String(id)) : []))
    : [];
  const playSessionId = String(d.playSessionId ?? d.PlaySessionId ?? '');
  if (!playSessionId) return null;
  return {
    playSessionId,
    panel,
    rounds,
    totalRounds: Number(d.totalRounds ?? d.TotalRounds) || 10,
    engineBootstrapCipher: (d.engineBootstrapCipher ?? d.EngineBootstrapCipher ?? null) as string | null,
  };
}

export const pvpService = {
  /** Any non-closed room the user is still in (host or participant). */
  getMyActivePvpRoom: async (): Promise<PvPRoom | null> => {
    const response = await api.get<PvPRoom>('/pvp/me/active-room', {
      validateStatus: (status) => status === 200 || status === 204
    });
    if (response.status === 204) return null;
    return response.data ?? null;
  },

  /** How many async rooms you host in lobby + which quiz ids — max 3, distinct quizzes enforced on create. */
  getHostedOpenAsyncSummary: async (): Promise<HostedOpenAsyncPvPSummary> => {
    const response = await api.get<HostedOpenAsyncPvPSummary>('/pvp/me/hosted-open-async');
    return response.data;
  },

  // Get PvP rooms with pagination and filters
  getPvPRooms: async (filters: PvPRoomFilters): Promise<PvPRoomListResponse> => {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.difficulty?.trim()) params.append('difficulty', filters.difficulty.trim());

    const response = await api.get(`/pvp/rooms?${params.toString()}`);
    return response.data;
  },

  getPvPRoom: async (roomId: string): Promise<PvPRoom> => {
    const response = await api.get<PvPRoom>(`/pvp/rooms/${roomId}`);
    return response.data;
  },

  getPvPRoomByRoomCode: async (roomCode: string): Promise<PvPRoom> => {
    const response = await api.get<PvPRoom>(`/pvp/rooms/code/${encodeURIComponent(roomCode)}`);
    return response.data;
  },

  /** Public: room must be AwaitingOpponent, not expired, no opponent yet. */
  getAsyncPvpInvitePreview: async (roomCode: string): Promise<PvpRoomInvitePreview> => {
    const response = await api.get<PvpRoomInvitePreview>(
      `/pvp/invite/async/${encodeURIComponent(roomCode)}`
    );
    return response.data;
  },

  getPvPRoomResult: async (roomId: string): Promise<PvPRoomResultDto> => {
    const response = await api.get<PvPRoomResultDto>(`/pvp/rooms/${roomId}/result`);
    return response.data;
  },

  /** Arena PvP: public room in Draft; one attempt debited. */
  createAsyncPvPRoom: async (body: {
    name: string;
    quizId: string;
    playDifficulty: string;
  }): Promise<PvPRoom> => {
    const response = await api.post<PvPRoom>('/pvp/async/rooms', body);
    return response.data;
  },

  /**
   * Join oldest open Arena room for this quiz + play band (FIFO by room creation), or `{ room: null }` when none.
   * Same auth and errors as `joinAsyncPvPRoom` when a matching room exists.
   */
  joinOldestForQuiz: async (body: {
    quizId: string;
    playDifficulty: string;
  }): Promise<{ room: PvPRoom | null }> => {
    const response = await api.post<{ room: PvPRoom | null }>(
      '/pvp/async/rooms/join-oldest-for-quiz',
      body
    );
    return response.data;
  },

  completeAsyncHostQuiz: async (
    roomId: string,
    body: {
      score: number;
      correctAnswers: number;
      totalQuestions: number;
      completionTime: string;
      skippedQuestions: number;
      /** Same order the host saw; persisted so the opponent gets identical questions/answers order. */
      questionIdsInOrder: string[];
      answerIdsPerQuestion: string[][];
      /** Per-question seconds (solo-equivalent speed bonus). */
      responseTimeSecondsPerQuestion: number[];
      correctPerQuestion: boolean[];
      /** Solo results modal: host XP already credited — only list in Arena. */
      hostPerformanceAlreadyCreditedFromSolo?: boolean;
      /** Genius PvP: 10 question ids in play order. */
      geniusSequenceQuestionIds?: string[];
      wordConnectFinishAudit?: WordConnectFinishAudit;
      wordConnectResults?: Array<{ pointsEarned: number; finalAnswerText?: string | null }>;
      mcFinishAudit?: McFinishAudit;
      stopFinishAudit?: StopFinishAudit;
      stopResults?: Array<{ pointsEarned: number; finalAnswerText?: string | null }>;
      tileMatchFinishAudit?: TileMatchFinishAudit;
      tileMatchResults?: Array<{ pointsEarned: number; finalAnswerText?: string | null }>;
      witcFinishAudit?: WitcFinishAudit;
      witcResults?: Array<{ pointsEarned: number; finalAnswerText?: string | null }>;
      geniusFinishAudit?: GeniusFinishAudit;
      geniusResults?: Array<{ pointsEarned: number; finalAnswerText?: string | null }>;
    },
    options?: { pwaClient?: boolean }
  ): Promise<PvPRoom> => {
    const headers: Record<string, string> = {};
    if (options?.pwaClient) {
      headers['X-Quizter-Client'] = 'pwa';
    }
    const response = await api.post<PvPRoom>(
      `/pvp/async/rooms/${roomId}/host-complete`,
      {
        roomId,
        score: body.score,
        correctAnswers: body.correctAnswers,
        totalQuestions: body.totalQuestions,
        completionTime: body.completionTime,
        skippedQuestions: body.skippedQuestions,
        questionIdsInOrder: body.questionIdsInOrder,
        answerIdsPerQuestion: body.answerIdsPerQuestion,
        responseTimeSecondsPerQuestion: body.responseTimeSecondsPerQuestion,
        correctPerQuestion: body.correctPerQuestion,
        hostPerformanceAlreadyCreditedFromSolo: body.hostPerformanceAlreadyCreditedFromSolo === true,
        geniusSequenceQuestionIds: body.geniusSequenceQuestionIds,
        wordConnectFinishAudit: body.wordConnectFinishAudit,
        wordConnectResults: body.wordConnectResults,
        mcFinishAudit: body.mcFinishAudit,
        stopFinishAudit: body.stopFinishAudit,
        stopResults: body.stopResults,
        tileMatchFinishAudit: body.tileMatchFinishAudit,
        tileMatchResults: body.tileMatchResults,
        witcFinishAudit: body.witcFinishAudit,
        witcResults: body.witcResults,
        geniusFinishAudit: body.geniusFinishAudit,
        geniusResults: body.geniusResults,
      },
      { headers: Object.keys(headers).length ? headers : undefined }
    );
    return response.data;
  },

  /** Genius async PvP: panel + rounds (host Draft or opponent from stored layout). */
  getAsyncPvPRoomGeniusPlay: async (roomId: string): Promise<GeniusPlayStart> => {
    const response = await api.get(`/pvp/async/rooms/${roomId}/genius-play`);
    const parsed = parseGeniusPlayStartFromApi(response.data);
    if (!parsed) throw new Error('Invalid Genius PvP play payload');
    return parsed;
  },

  /** Word Connect async PvP: puzzle grid layout (host Draft or opponent from stored layout). */
  getAsyncPvPRoomWordConnectPlay: async (roomId: string): Promise<WordConnectPlayStart> => {
    const response = await api.get(`/pvp/async/rooms/${roomId}/word-connect-play`);
    const parsed = parseWordConnectPlayStartFromApi(response.data);
    if (!parsed) throw new Error('Invalid Word Connect PvP play payload');
    return parsed;
  },

  /** Same quiz layout the host played (assigned opponent, after join). */
  getAsyncPvPRoomQuizQuestions: async (roomId: string): Promise<Question[]> => {
    const response = await api.get<Question[]>(`/pvp/async/rooms/${roomId}/quiz-questions`);
    return response.data;
  },

  /** Multiple Choice async PvP: quiz layout (host Draft or opponent from stored layout). */
  getAsyncPvPRoomMcPlay: async (roomId: string): Promise<McPvpPlayStart> => {
    const response = await api.get<McPvpPlayStart>(`/pvp/async/rooms/${roomId}/mc-play`);
    return response.data;
  },

  /** Stop async PvP: letter pool and session details (host Draft or opponent from stored layout). */
  getAsyncPvPRoomStopPlay: async (roomId: string): Promise<StopPlayStart> => {
    const response = await api.get(`/pvp/async/rooms/${roomId}/stop-play`);
    return response.data;
  },

  /** Tile Match async PvP: layout and details (host Draft or opponent from stored layout). */
  getAsyncPvPRoomTileMatchPlay: async (roomId: string): Promise<TileMatchPlayStart> => {
    const response = await api.get<TileMatchPlayStart>(`/pvp/async/rooms/${roomId}/tile-match-play`);
    return response.data;
  },

  /** WITC async PvP: silhouette assets and play session details (host Draft or opponent from stored layout). */
  getAsyncPvPRoomWitcPlay: async (roomId: string): Promise<WitcPlayStart> => {
    const response = await api.get<WitcPlayStart>(`/pvp/async/rooms/${roomId}/witc-play`);
    return response.data;
  },

  joinAsyncPvPRoom: async (roomId: string): Promise<PvPRoom> => {
    const response = await api.post<PvPRoom>(`/pvp/async/rooms/${roomId}/join`);
    return response.data;
  },

  completeAsyncOpponentQuiz: async (
    roomId: string,
    body: {
      score: number;
      correctAnswers: number;
      totalQuestions: number;
      completionTime: string;
      skippedQuestions: number;
      responseTimeSecondsPerQuestion: number[];
      correctPerQuestion: boolean[];
      wordConnectFinishAudit?: WordConnectFinishAudit;
      wordConnectResults?: Array<{ pointsEarned: number; finalAnswerText?: string | null }>;
      mcFinishAudit?: McFinishAudit;
      stopFinishAudit?: StopFinishAudit;
      stopResults?: Array<{ pointsEarned: number; finalAnswerText?: string | null }>;
      tileMatchFinishAudit?: TileMatchFinishAudit;
      tileMatchResults?: Array<{ pointsEarned: number; finalAnswerText?: string | null }>;
      witcFinishAudit?: WitcFinishAudit;
      witcResults?: Array<{ pointsEarned: number; finalAnswerText?: string | null }>;
      geniusFinishAudit?: GeniusFinishAudit;
      geniusResults?: Array<{ pointsEarned: number; finalAnswerText?: string | null }>;
    },
    options?: { pwaClient?: boolean }
  ): Promise<PvPRoom> => {
    const headers: Record<string, string> = {};
    if (options?.pwaClient) {
      headers['X-Quizter-Client'] = 'pwa';
    }
    const response = await api.post<PvPRoom>(
      `/pvp/async/rooms/${roomId}/opponent-complete`,
      {
        roomId,
        score: body.score,
        correctAnswers: body.correctAnswers,
        totalQuestions: body.totalQuestions,
        completionTime: body.completionTime,
        skippedQuestions: body.skippedQuestions,
        responseTimeSecondsPerQuestion: body.responseTimeSecondsPerQuestion,
        correctPerQuestion: body.correctPerQuestion,
        wordConnectFinishAudit: body.wordConnectFinishAudit,
        wordConnectResults: body.wordConnectResults,
        mcFinishAudit: body.mcFinishAudit,
        stopFinishAudit: body.stopFinishAudit,
        stopResults: body.stopResults,
        tileMatchFinishAudit: body.tileMatchFinishAudit,
        tileMatchResults: body.tileMatchResults,
        witcFinishAudit: body.witcFinishAudit,
        witcResults: body.witcResults,
        geniusFinishAudit: body.geniusFinishAudit,
        geniusResults: body.geniusResults,
      },
      { headers: Object.keys(headers).length ? headers : undefined }
    );
    return response.data;
  },

  abandonAsyncDraft: async (roomId: string): Promise<void> => {
    await api.post(`/pvp/async/rooms/${roomId}/abandon-draft`);
  },

  cancelAsyncPublishedRoom: async (roomId: string): Promise<void> => {
    await api.post(`/pvp/async/rooms/${roomId}/cancel-published`);
  },

  abandonAsyncOpponentQuiz: async (roomId: string): Promise<void> => {
    await api.post(`/pvp/async/rooms/${roomId}/abandon-opponent-quiz`);
  },
};

export default api; 